import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Checkbox,
  FormControlLabel,
  AccordionSummary,
  AccordionDetails,
  Accordion,
  FormControl,
  IconButton,
  Tooltip,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Visibility,
  Assessment,
  Person,
  Business,
  Print,
  PrintOutlined,
  Compare,
  ExpandMore,
  TrendingUp,
  Edit,
  Delete,
  DeleteOutline,
  Science,
  BarChart,
  CheckCircle,
  HourglassEmpty,
  ContactPhone,
} from '@mui/icons-material';
import { wrappedAdminSessionAPI, wrappedAdminQuizLeadsAPI } from './services/admin-api';
import { useLanguage } from './contexts/LanguageContext';
import EditParticipantDialog from './components/EditParticipantDialog';
import EpokAnalysisView from './components/EpokAnalysisView';
import GaugesDashboard from './components/GaugesDashboard';
import ProgressBarsDashboard from './components/ProgressBarsDashboard';
import CircularDashboard from './components/CircularDashboard';
import RadarChartDashboard from './components/RadarChartDashboard';
import HorizontalBarDashboard from './components/HorizontalBarDashboard';
import HeatMapDashboard from './components/HeatMapDashboard';
import SpeedometerDashboard from './components/SpeedometerDashboard';
import ColumnChartDashboard from './components/ColumnChartDashboard';
import ScoreCardDashboard from './components/ScoreCardDashboard';
import { httpsCallable } from 'firebase/functions';
import { functions } from './config/firebase';
import { toast } from 'react-toastify';

interface Session {
  id: string;
  assessment: {
    title: string;
    type: string;
  };
  participantName: string;
  participantEmail: string;
  participantPhone?: string;
  participantOrganization?: string;
  participantPosition?: string;
  participantAddress?: string;
  participantEmployeeCount?: string;
  status: string;
  startedAt: string;
  completedAt: string;
  createdAt?: string;
  currentPage?: number;
  chartImage?: string;
}

interface QuizLead {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  // All quiz answers stored in answers object
  answers?: Record<string, any>;
  // Extracted key fields
  challenges?: string | string[];
  duration?: string;
  tried?: string | string[];
  whyNotWorked?: string;
  whyNow?: string;
  urgency?: string;
  businessField?: string;
  teamSize?: string;
  important?: string;
  potentialRevenue?: string;
  currentRevenue?: string;
  consulting?: string;
  expectations?: string | string[];
  deepAnalysis?: string;
  // Metadata
  language: string;
  status: string;
  wantsDeepAnalysis: boolean;
  createdAt: string;
  updatedAt: string;
}

interface AdminPageProps {
}

const AdminPage: React.FC<AdminPageProps> = () => {
  const { language, t } = useLanguage();
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [sessionDetails, setSessionDetails] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [chartImage, setChartImage] = useState<string | null>(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // Chart view type: all available chart variations
  type ChartViewType = 'classic' | 'gauges' | 'progress' | 'circular' | 'radar' | 'horizontalBar' | 'heatMap' | 'speedometer' | 'column' | 'scoreCard';
  const [chartViewType, setChartViewType] = useState<ChartViewType>('classic');

  // Comparison functionality
  const [selectedParticipants, setSelectedParticipants] = useState<Set<string>>(new Set());
  const [comparisonDialogOpen, setComparisonDialogOpen] = useState(false);

  // Edit participant dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [sessionToEdit, setSessionToEdit] = useState<Session | null>(null);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<Session | null>(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [deletingLoading, setDeletingLoading] = useState(false);
  const [comparisonChartImage, setComparisonChartImage] = useState<string | null>(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);

  // Organization filter
  const [selectedOrganization, setSelectedOrganization] = useState<string>('');
  const [organizations, setOrganizations] = useState<string[]>([]);

  // Session status filter: 'completed', 'unfinished', or 'leads'
  const [sessionFilter, setSessionFilter] = useState<'completed' | 'unfinished' | 'leads'>('completed');

  // Quiz leads state
  const [quizLeads, setQuizLeads] = useState<QuizLead[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState<QuizLead | null>(null);
  const [leadDetailsOpen, setLeadDetailsOpen] = useState(false);

  const loadQuizLeads = useCallback(async () => {
    try {
      setLeadsLoading(true);
      const result = await wrappedAdminQuizLeadsAPI.getQuizLeads();
      const data = result.data as { leads: QuizLead[] };
      setQuizLeads(data.leads || []);
    } catch (error: any) {
      console.error('Failed to load quiz leads:', error);
      toast.error('Failed to load quiz leads');
    } finally {
      setLeadsLoading(false);
    }
  }, []);

  const loadSessions = useCallback(async () => {
    try {
      setLoading(true);
      let result;
      if (sessionFilter === 'unfinished') {
        result = await wrappedAdminSessionAPI.getUnfinishedSessions(selectedOrganization);
      } else if (sessionFilter === 'completed') {
        result = await wrappedAdminSessionAPI.getCompletedSessions(selectedOrganization);
      } else {
        // For 'leads', we load separately
        setLoading(false);
        return;
      }
      const data = result.data as Session[];
      setSessions(data || []);
      setError('');
    } catch (error: any) {
      console.error('Failed to load sessions:', error);
      setError(t['admin.error']);
    } finally {
      setLoading(false);
    }
  }, [selectedOrganization, sessionFilter, t]);

  const loadOrganizations = useCallback(async () => {
    try {
      // Just use the wrapped API - let's see what it actually does
      const result = await wrappedAdminSessionAPI.getOrganizations();
      setOrganizations(result.data || []);
    } catch (error) {
      console.error('Failed to load organizations:', error);
    }
  }, []);

  // Load organizations only once on mount
  useEffect(() => {
    loadOrganizations();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load sessions or leads whenever filter changes
  useEffect(() => {
    if (sessionFilter === 'leads') {
      loadQuizLeads();
    } else {
      loadSessions();
    }
  }, [loadSessions]);

  // Function to generate progress bars dashboard HTML for printing
  const generateProgressBarsDashboardHTML = (categoryScores: Record<string, number>, language: string) => {
    const categoryNames: Record<string, { hebrew: string; english: string }> = {
      'management': { hebrew: 'ניהול', english: 'Executive Management' },
      'organization': { hebrew: 'ארגון', english: 'Organization' },
      'repeatSales': { hebrew: 'מכירות חוזרות', english: 'Repeat Sales' },
      'finance': { hebrew: 'כספים', english: 'Finance' },
      'operations': { hebrew: 'תפעול', english: 'Operations' },
      'qualityControl': { hebrew: 'בקרת איכות', english: 'Quality Control' },
      'newSales': { hebrew: 'מכירות חדשות', english: 'New Sales' },
      'stress': { hebrew: 'מתח', english: 'Stress Management' },
      'principles': { hebrew: 'עקרונות בסיסיים', english: 'Core Principles' },
      'helpAcceptance': { hebrew: 'קבלת עזרה', english: 'Help Acceptance' },
    };

    const scores = Object.entries(categoryScores).map(([_, score], index) => {
      const categoryKeys = Object.keys(categoryNames);
      const categoryKey = categoryKeys[index] || 'management';
      return {
        key: categoryKey,
        name: language === 'he' ? categoryNames[categoryKey]?.hebrew : categoryNames[categoryKey]?.english,
        value: score as number
      };
    });

    const businessScores = scores.slice(0, 7);
    const personalScores = scores.slice(7, 10);
    const businessAverage = businessScores.reduce((sum, cat) => sum + cat.value, 0) / 7;
    const personalAverage = personalScores.reduce((sum, cat) => sum + cat.value, 0) / 3;

    const getScoreColor = (score: number) => {
      if (score >= 80) return '#27ae60';
      if (score >= 60) return '#2ecc71';
      if (score >= 40) return '#f39c12';
      return '#e74c3c';
    };

    const getScoreStatus = (score: number) => {
      if (language === 'he') {
        if (score >= 80) return 'מצוין';
        if (score >= 60) return 'טוב';
        if (score >= 40) return 'בינוני';
        return 'טעון שיפור';
      } else {
        if (score >= 80) return 'Excellent';
        if (score >= 60) return 'Good';
        if (score >= 40) return 'Fair';
        return 'Needs Work';
      }
    };

    const getTrendIcon = (score: number) => {
      if (score >= 70) return '↗';
      if (score >= 50) return '→';
      return '↘';
    };

    const renderBar = (cat: any) => `
      <div class="progress-card">
        <div class="card-header">
          <div class="card-title">
            <span class="trend-icon" style="color: ${getScoreColor(cat.value)}">${getTrendIcon(cat.value)}</span>
            <span class="category-name">${cat.name}</span>
          </div>
          <div class="card-values">
            <span class="status-badge" style="background-color: ${getScoreColor(cat.value)}">${getScoreStatus(cat.value)}</span>
            <span class="score-value" style="color: ${getScoreColor(cat.value)}">${cat.value.toFixed(0)}%</span>
          </div>
        </div>
        <div class="progress-container">
          <div class="progress-bar" style="width: ${cat.value}%; background-color: ${getScoreColor(cat.value)}"></div>
          <div class="reference-line"></div>
        </div>
      </div>
    `;

    return `
      <style>
        .progress-dashboard { width: 100%; padding: 20px; font-family: Arial, sans-serif; }
        .summary-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
        .summary-card { padding: 20px; border-radius: 8px; text-align: center; }
        .summary-card.business { background: linear-gradient(135deg, #ffe5e5 0%, #ffcccc 100%); border: 2px solid #e74c3c; }
        .summary-card.personal { background: linear-gradient(135deg, #e5f5e5 0%, #ccf0cc 100%); border: 2px solid #27ae60; }
        .summary-icon { font-size: 32px; margin-bottom: 8px; }
        .summary-title { font-size: 18px; font-weight: bold; margin-bottom: 8px; }
        .summary-value { font-size: 36px; font-weight: bold; }
        .summary-subtitle { font-size: 12px; color: #666; margin-top: 4px; }
        .category-section { margin-bottom: 30px; }
        .section-title { font-size: 20px; font-weight: bold; margin-bottom: 15px; border-bottom: 2px solid #333; padding-bottom: 8px; }
        .progress-card { background: white; border: 1px solid #ddd; border-radius: 8px; padding: 16px; margin-bottom: 12px; }
        .card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
        .card-title { display: flex; align-items: center; gap: 8px; }
        .trend-icon { font-size: 20px; font-weight: bold; }
        .category-name { font-size: 16px; font-weight: 600; }
        .card-values { display: flex; align-items: center; gap: 12px; }
        .status-badge { padding: 4px 12px; border-radius: 12px; color: white; font-size: 12px; font-weight: bold; }
        .score-value { font-size: 18px; font-weight: bold; min-width: 50px; text-align: right; }
        .progress-container { position: relative; height: 12px; background-color: #e0e0e0; border-radius: 6px; overflow: hidden; }
        .progress-bar { height: 100%; border-radius: 6px; transition: width 0.3s; }
        .reference-line { position: absolute; left: 60%; top: 0; bottom: 0; width: 2px; background-color: rgba(0,0,0,0.3); }
      </style>
      <div class="progress-dashboard" style="direction: ${language === 'he' ? 'rtl' : 'ltr'}">
        <div class="summary-cards">
          <div class="summary-card business">
            <div class="summary-icon">🏢</div>
            <div class="summary-title">${language === 'he' ? 'ממוצע עסקי' : 'Business Average'}</div>
            <div class="summary-value" style="color: ${getScoreColor(businessAverage)}">${businessAverage.toFixed(1)}%</div>
            <div class="summary-subtitle">${language === 'he' ? 'מבוסס על 7 קטגוריות' : 'Based on 7 categories'}</div>
          </div>
          <div class="summary-card personal">
            <div class="summary-icon">👤</div>
            <div class="summary-title">${language === 'he' ? 'ממוצע אישי' : 'Personal Average'}</div>
            <div class="summary-value" style="color: ${getScoreColor(personalAverage)}">${personalAverage.toFixed(1)}%</div>
            <div class="summary-subtitle">${language === 'he' ? 'מבוסס על 3 קטגוריות' : 'Based on 3 categories'}</div>
          </div>
        </div>
        <div class="category-section">
          <div class="section-title">🏢 ${language === 'he' ? 'קטגוריות עסקיות' : 'Business Categories'}</div>
          ${businessScores.map(renderBar).join('')}
        </div>
        <div class="category-section">
          <div class="section-title">👤 ${language === 'he' ? 'קטגוריות אישיות' : 'Personal Categories'}</div>
          ${personalScores.map(renderBar).join('')}
        </div>
      </div>
    `;
  };

  // Function to generate circular dashboard HTML for printing
  const generateCircularDashboardHTML = (categoryScores: Record<string, number>, language: string) => {
    const categoryNames: Record<string, { hebrew: string; english: string; icon: string }> = {
      'management': { hebrew: 'ניהול', english: 'Management', icon: '🎯' },
      'organization': { hebrew: 'ארגון', english: 'Organization', icon: '🏢' },
      'repeatSales': { hebrew: 'מכירות חוזרות', english: 'Repeat Sales', icon: '🔄' },
      'finance': { hebrew: 'כספים', english: 'Finance', icon: '💰' },
      'operations': { hebrew: 'תפעול', english: 'Operations', icon: '⚙️' },
      'qualityControl': { hebrew: 'בקרת איכות', english: 'Quality Control', icon: '✅' },
      'newSales': { hebrew: 'מכירות חדשות', english: 'New Sales', icon: '📈' },
      'stress': { hebrew: 'מתח', english: 'Stress', icon: '😰' },
      'principles': { hebrew: 'עקרונות', english: 'Principles', icon: '🧭' },
      'helpAcceptance': { hebrew: 'קבלת עזרה', english: 'Help', icon: '🤝' },
    };

    const scores = Object.entries(categoryScores).map(([_, score], index) => {
      const categoryKeys = Object.keys(categoryNames);
      const categoryKey = categoryKeys[index] || 'management';
      return {
        key: categoryKey,
        name: language === 'he' ? categoryNames[categoryKey]?.hebrew : categoryNames[categoryKey]?.english,
        icon: categoryNames[categoryKey]?.icon || '📊',
        value: score as number
      };
    });

    const businessScores = scores.slice(0, 7);
    const personalScores = scores.slice(7, 10);
    const businessAverage = businessScores.reduce((sum, cat) => sum + cat.value, 0) / 7;
    const personalAverage = personalScores.reduce((sum, cat) => sum + cat.value, 0) / 3;

    const getScoreColor = (score: number) => {
      if (score >= 80) return '#27ae60';
      if (score >= 60) return '#2ecc71';
      if (score >= 40) return '#f39c12';
      return '#e74c3c';
    };

    const getStatusIcon = (score: number) => {
      if (score >= 80) return '✅';
      if (score >= 60) return '✓';
      if (score >= 40) return '⚠️';
      return '❌';
    };

    const renderCircle = (cat: any, size: number = 100) => {
      const circumference = 2 * Math.PI * 40;
      const offset = circumference - (cat.value / 100) * circumference;
      return `
        <div class="circular-card">
          <div class="circular-wrapper">
            <svg width="${size}" height="${size}" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="40" fill="none" stroke="#e0e0e0" stroke-width="8"/>
              <circle cx="50" cy="50" r="40" fill="none"
                      stroke="${getScoreColor(cat.value)}"
                      stroke-width="8"
                      stroke-dasharray="${circumference}"
                      stroke-dashoffset="${offset}"
                      stroke-linecap="round"
                      transform="rotate(-90 50 50)"/>
            </svg>
            <div class="circle-content">
              <div class="circle-icon">${cat.icon}</div>
              <div class="circle-value">${Math.round(cat.value)}%</div>
            </div>
          </div>
          <div class="circle-label">${cat.name}</div>
          <div class="circle-status">${getStatusIcon(cat.value)}</div>
        </div>
      `;
    };

    const renderMasterGauge = (average: number, title: string, icon: string, bgClass: string) => `
      <div class="master-gauge ${bgClass}">
        <div class="master-icon">${icon}</div>
        <div class="master-title">${title}</div>
        <div class="master-circle">
          <svg width="140" height="140" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(255,255,255,0.3)" stroke-width="8"/>
            <circle cx="50" cy="50" r="40" fill="none"
                    stroke="${getScoreColor(average)}"
                    stroke-width="8"
                    stroke-dasharray="${2 * Math.PI * 40}"
                    stroke-dashoffset="${2 * Math.PI * 40 - (average / 100) * 2 * Math.PI * 40}"
                    stroke-linecap="round"
                    transform="rotate(-90 50 50)"/>
          </svg>
          <div class="master-value">${Math.round(average)}%</div>
        </div>
        <div class="master-subtitle">${language === 'he' ? 'מבוסס על' : 'Based on'} ${bgClass === 'business' ? 7 : 3} ${language === 'he' ? 'קטגוריות' : 'categories'}</div>
      </div>
    `;

    return `
      <style>
        .circular-dashboard { width: 100%; padding: 20px; font-family: Arial, sans-serif; }
        .master-gauges { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
        .master-gauge { padding: 24px; border-radius: 12px; text-align: center; }
        .master-gauge.business { background: linear-gradient(135deg, #ffe5e5 0%, #ffcccc 100%); border: 2px solid #e74c3c; }
        .master-gauge.personal { background: linear-gradient(135deg, #e5f5e5 0%, #ccf0cc 100%); border: 2px solid #27ae60; }
        .master-icon { font-size: 32px; margin-bottom: 8px; }
        .master-title { font-size: 20px; font-weight: bold; margin-bottom: 16px; }
        .master-circle { position: relative; display: inline-block; margin: 8px 0; }
        .master-value { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 32px; font-weight: bold; }
        .master-subtitle { font-size: 12px; color: #666; margin-top: 8px; }
        .category-section { margin-bottom: 30px; }
        .section-title { font-size: 20px; font-weight: bold; margin-bottom: 15px; border-bottom: 2px solid #333; padding-bottom: 8px; }
        .circles-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 20px; }
        .circular-card { text-align: center; padding: 12px; background: white; border: 1px solid #ddd; border-radius: 8px; }
        .circular-wrapper { position: relative; display: inline-block; }
        .circle-content { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); }
        .circle-icon { font-size: 28px; }
        .circle-value { font-size: 16px; font-weight: bold; margin-top: 4px; }
        .circle-label { font-size: 14px; font-weight: 600; margin-top: 8px; }
        .circle-status { font-size: 20px; margin-top: 4px; }
      </style>
      <div class="circular-dashboard" style="direction: ${language === 'he' ? 'rtl' : 'ltr'}">
        <div class="master-gauges">
          ${renderMasterGauge(businessAverage, language === 'he' ? 'ממוצע עסקי' : 'Business Average', '🏢', 'business')}
          ${renderMasterGauge(personalAverage, language === 'he' ? 'ממוצע אישי' : 'Personal Average', '👤', 'personal')}
        </div>
        <div class="category-section">
          <div class="section-title">🏢 ${language === 'he' ? 'קטגוריות עסקיות' : 'Business Categories'}</div>
          <div class="circles-grid">
            ${businessScores.map(cat => renderCircle(cat)).join('')}
          </div>
        </div>
        <div class="category-section">
          <div class="section-title">👤 ${language === 'he' ? 'קטגוריות אישיות' : 'Personal Categories'}</div>
          <div class="circles-grid">
            ${personalScores.map(cat => renderCircle(cat)).join('')}
          </div>
        </div>
      </div>
    `;
  };

  // Function to generate radar chart dashboard HTML for printing
  const generateRadarChartDashboardHTML = (categoryScores: Record<string, number>, language: string) => {
    const categoryNames: Record<string, { hebrew: string; english: string }> = {
      'management': { hebrew: 'ניהול', english: 'Management' },
      'organization': { hebrew: 'ארגון', english: 'Organization' },
      'repeatSales': { hebrew: 'מכירות חוזרות', english: 'Repeat Sales' },
      'finance': { hebrew: 'כספים', english: 'Finance' },
      'operations': { hebrew: 'תפעול', english: 'Operations' },
      'qualityControl': { hebrew: 'בקרת איכות', english: 'Quality Control' },
      'newSales': { hebrew: 'מכירות חדשות', english: 'New Sales' },
      'stress': { hebrew: 'מתח', english: 'Stress' },
      'principles': { hebrew: 'עקרונות', english: 'Principles' },
      'helpAcceptance': { hebrew: 'קבלת עזרה', english: 'Help' },
    };

    const scores = Object.entries(categoryScores).map(([_, score], index) => {
      const categoryKeys = Object.keys(categoryNames);
      const categoryKey = categoryKeys[index] || 'management';
      return {
        name: language === 'he' ? categoryNames[categoryKey]?.hebrew : categoryNames[categoryKey]?.english,
        value: score as number
      };
    });

    const businessScores = scores.slice(0, 7);
    const personalScores = scores.slice(7, 10);
    const businessAverage = businessScores.reduce((sum, cat) => sum + cat.value, 0) / 7;
    const personalAverage = personalScores.reduce((sum, cat) => sum + cat.value, 0) / 3;

    const drawRadarChart = (data: any[], cx: number, cy: number, r: number) => {
      const numPoints = data.length;
      const angleStep = (2 * Math.PI) / numPoints;

      const gridCircles = [20, 40, 60, 80, 100].map(pct =>
        `<circle cx="${cx}" cy="${cy}" r="${(r * pct) / 100}" fill="none" stroke="#e0e0e0" stroke-width="1"/>`
      ).join('');

      const points = data.map((item, i) => {
        const angle = i * angleStep - Math.PI / 2;
        const distance = (r * item.value) / 100;
        return `${cx + distance * Math.cos(angle)},${cy + distance * Math.sin(angle)}`;
      }).join(' ');

      return `${gridCircles}<polygon points="${points}" fill="rgba(52,152,219,0.3)" stroke="#3498db" stroke-width="2"/>`;
    };

    return `
      <style>
        .radar-dashboard { width: 100%; padding: 20px; font-family: Arial, sans-serif; }
        .radar-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
        .radar-card { padding: 20px; border: 2px solid #ddd; border-radius: 8px; text-align: center; }
      </style>
      <div class="radar-dashboard" style="direction: ${language === 'he' ? 'rtl' : 'ltr'}">
        <h3 style="text-align: center; margin-bottom: 20px;">📊 ${language === 'he' ? 'ניתוח רדאר מקיף' : 'Comprehensive Radar Analysis'}</h3>
        <div class="radar-grid">
          <div class="radar-card">
            <h4 style="color: #3498db;">🏢 ${language === 'he' ? 'מפת רדאר עסקית' : 'Business Radar Map'}</h4>
            <svg width="400" height="400" viewBox="0 0 400 400">
              ${drawRadarChart(businessScores, 200, 200, 140)}
            </svg>
            <p>${language === 'he' ? 'ממוצע עסקי:' : 'Business Average:'} <strong>${businessAverage.toFixed(1)}%</strong></p>
          </div>
          <div class="radar-card">
            <h4 style="color: #9b59b6;">👤 ${language === 'he' ? 'מפת רדאר אישית' : 'Personal Radar Map'}</h4>
            <svg width="400" height="400" viewBox="0 0 400 400">
              ${drawRadarChart(personalScores, 200, 200, 140)}
            </svg>
            <p>${language === 'he' ? 'ממוצע אישי:' : 'Personal Average:'} <strong>${personalAverage.toFixed(1)}%</strong></p>
          </div>
        </div>
      </div>
    `;
  };

  // Function to generate horizontal bar dashboard HTML for printing
  const generateHorizontalBarDashboardHTML = (categoryScores: Record<string, number>, language: string) => {
    const categoryNames: Record<string, { hebrew: string; english: string }> = {
      'management': { hebrew: 'ניהול', english: 'Management' },
      'organization': { hebrew: 'ארגון', english: 'Organization' },
      'repeatSales': { hebrew: 'מכירות חוזרות', english: 'Repeat Sales' },
      'finance': { hebrew: 'כספים', english: 'Finance' },
      'operations': { hebrew: 'תפעול', english: 'Operations' },
      'qualityControl': { hebrew: 'בקרת איכות', english: 'Quality Control' },
      'newSales': { hebrew: 'מכירות חדשות', english: 'New Sales' },
      'stress': { hebrew: 'מתח', english: 'Stress' },
      'principles': { hebrew: 'עקרונות', english: 'Principles' },
      'helpAcceptance': { hebrew: 'קבלת עזרה', english: 'Help' },
    };

    const scores = Object.entries(categoryScores).map(([_, score], index) => {
      const categoryKeys = Object.keys(categoryNames);
      const categoryKey = categoryKeys[index] || 'management';
      return {
        name: language === 'he' ? categoryNames[categoryKey]?.hebrew : categoryNames[categoryKey]?.english,
        value: score as number
      };
    });

    const businessScores = scores.slice(0, 7);
    const personalScores = scores.slice(7, 10);
    const businessAverage = businessScores.reduce((sum, cat) => sum + cat.value, 0) / 7;
    const personalAverage = personalScores.reduce((sum, cat) => sum + cat.value, 0) / 3;

    const getColor = (score: number) => score >= 80 ? '#27ae60' : score >= 60 ? '#2ecc71' : score >= 40 ? '#f39c12' : '#e74c3c';

    const renderBar = (cat: any, idx: number) => `
      <div style="margin-bottom: 15px;">
        <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
          <span style="font-weight: 600;">${idx + 1}. ${cat.name}</span>
          <span style="font-weight: bold; color: ${getColor(cat.value)};">${cat.value.toFixed(0)}%</span>
        </div>
        <div style="height: 32px; background: #f5f5f5; border-radius: 4px; position: relative; overflow: hidden;">
          <div style="height: 100%; width: ${cat.value}%; background: linear-gradient(90deg, ${getColor(cat.value)}dd, ${getColor(cat.value)}); border-radius: 4px;"></div>
        </div>
      </div>
    `;

    return `
      <style>
        .hbar-dashboard { width: 100%; padding: 20px; font-family: Arial, sans-serif; }
        .summary-row { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
        .summary-box { padding: 20px; border-radius: 8px; text-align: center; color: white; }
      </style>
      <div class="hbar-dashboard" style="direction: ${language === 'he' ? 'rtl' : 'ltr'}">
        <h3 style="text-align: center;">📊 ${language === 'he' ? 'ניתוח השוואתי עם בנצ\'מארק' : 'Comparative Benchmark Analysis'}</h3>
        <div class="summary-row">
          <div class="summary-box" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);">
            <h4>🏢 ${language === 'he' ? 'ביצועים עסקיים' : 'Business Performance'}</h4>
            <h2>${businessAverage.toFixed(1)}%</h2>
          </div>
          <div class="summary-box" style="background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);">
            <h4>👤 ${language === 'he' ? 'ביצועים אישיים' : 'Personal Performance'}</h4>
            <h2>${personalAverage.toFixed(1)}%</h2>
          </div>
        </div>
        <div style="padding: 20px; border: 1px solid #ddd; border-radius: 8px; margin-bottom: 20px;">
          <h4>🏢 ${language === 'he' ? 'קטגוריות עסקיות' : 'Business Categories'}</h4>
          ${businessScores.map((cat, idx) => renderBar(cat, idx)).join('')}
        </div>
        <div style="padding: 20px; border: 1px solid #ddd; border-radius: 8px;">
          <h4>👤 ${language === 'he' ? 'קטגוריות אישיות' : 'Personal Categories'}</h4>
          ${personalScores.map((cat, idx) => renderBar(cat, idx)).join('')}
        </div>
      </div>
    `;
  };

  // Function to generate heat map dashboard HTML for printing
  const generateHeatMapDashboardHTML = (categoryScores: Record<string, number>, language: string) => {
    const categoryNames: Record<string, { hebrew: string; english: string; icon: string }> = {
      'management': { hebrew: 'ניהול', english: 'Management', icon: '🎯' },
      'organization': { hebrew: 'ארגון', english: 'Organization', icon: '🏢' },
      'repeatSales': { hebrew: 'מכירות חוזרות', english: 'Repeat Sales', icon: '🔄' },
      'finance': { hebrew: 'כספים', english: 'Finance', icon: '💰' },
      'operations': { hebrew: 'תפעול', english: 'Operations', icon: '⚙️' },
      'qualityControl': { hebrew: 'בקרת איכות', english: 'Quality Control', icon: '✅' },
      'newSales': { hebrew: 'מכירות חדשות', english: 'New Sales', icon: '📈' },
      'stress': { hebrew: 'מתח', english: 'Stress', icon: '😰' },
      'principles': { hebrew: 'עקרונות', english: 'Principles', icon: '🧭' },
      'helpAcceptance': { hebrew: 'קבלת עזרה', english: 'Help', icon: '🤝' },
    };

    const scores = Object.entries(categoryScores).map(([_, score], index) => {
      const categoryKeys = Object.keys(categoryNames);
      const categoryKey = categoryKeys[index] || 'management';
      return {
        name: language === 'he' ? categoryNames[categoryKey]?.hebrew : categoryNames[categoryKey]?.english,
        icon: categoryNames[categoryKey]?.icon || '📊',
        value: score as number
      };
    });

    const businessScores = scores.slice(0, 7);
    const personalScores = scores.slice(7, 10);
    const overallAverage = scores.reduce((sum, cat) => sum + cat.value, 0) / scores.length;

    const getHeatColor = (score: number) => {
      if (score >= 80) return '#27ae60';
      if (score >= 70) return '#2ecc71';
      if (score >= 60) return '#52c77c';
      if (score >= 50) return '#f39c12';
      if (score >= 40) return '#e67e22';
      return '#e74c3c';
    };

    const renderCell = (cat: any) => `
      <div style="padding: 15px; background: ${getHeatColor(cat.value)}; color: white; text-align: center; border-radius: 8px; min-height: 120px; display: flex; flex-direction: column; justify-content: center;">
        <div style="font-size: 32px;">${cat.icon}</div>
        <div style="font-weight: 600; margin-top: 8px; font-size: 0.9rem;">${cat.name}</div>
        <div style="font-size: 24px; font-weight: bold; margin-top: 4px;">${cat.value.toFixed(0)}%</div>
      </div>
    `;

    return `
      <style>
        .heatmap-dashboard { width: 100%; padding: 20px; font-family: Arial, sans-serif; }
        .heat-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 15px; }
      </style>
      <div class="heatmap-dashboard" style="direction: ${language === 'he' ? 'rtl' : 'ltr'}">
        <h3 style="text-align: center;">🗺️ ${language === 'he' ? 'מפת חום - תצוגת מטריצה' : 'Heat Map - Matrix View'}</h3>
        <div style="padding: 30px; background: linear-gradient(135deg, ${getHeatColor(overallAverage)}dd, ${getHeatColor(overallAverage)}); color: white; border-radius: 8px; text-align: center; margin-bottom: 30px;">
          <h4>${language === 'he' ? 'ציון כולל' : 'Overall Score'}</h4>
          <h1 style="margin: 10px 0;">${overallAverage.toFixed(1)}%</h1>
        </div>
        <div style="margin-bottom: 30px;">
          <h4>🏢 ${language === 'he' ? 'קטגוריות עסקיות' : 'Business Categories'}</h4>
          <div class="heat-grid">${businessScores.map(renderCell).join('')}</div>
        </div>
        <div>
          <h4>👤 ${language === 'he' ? 'קטגוריות אישיות' : 'Personal Categories'}</h4>
          <div class="heat-grid">${personalScores.map(renderCell).join('')}</div>
        </div>
      </div>
    `;
  };

  // Function to generate speedometer dashboard HTML for printing
  const generateSpeedometerDashboardHTML = (categoryScores: Record<string, number>, language: string) => {
    const categoryNames: Record<string, { hebrew: string; english: string }> = {
      'management': { hebrew: 'ניהול', english: 'Management' },
      'organization': { hebrew: 'ארגון', english: 'Organization' },
      'repeatSales': { hebrew: 'מכירות חוזרות', english: 'Repeat Sales' },
      'finance': { hebrew: 'כספים', english: 'Finance' },
      'operations': { hebrew: 'תפעול', english: 'Operations' },
      'qualityControl': { hebrew: 'בקרת איכות', english: 'Quality Control' },
      'newSales': { hebrew: 'מכירות חדשות', english: 'New Sales' },
      'stress': { hebrew: 'מתח', english: 'Stress' },
      'principles': { hebrew: 'עקרונות', english: 'Principles' },
      'helpAcceptance': { hebrew: 'קבלת עזרה', english: 'Help' },
    };

    const scores = Object.entries(categoryScores).map(([_, score], index) => {
      const categoryKeys = Object.keys(categoryNames);
      const categoryKey = categoryKeys[index] || 'management';
      return {
        name: language === 'he' ? categoryNames[categoryKey]?.hebrew : categoryNames[categoryKey]?.english,
        value: score as number
      };
    });

    const businessScores = scores.slice(0, 7);
    const personalScores = scores.slice(7, 10);
    const businessAverage = businessScores.reduce((sum, cat) => sum + cat.value, 0) / 7;
    const personalAverage = personalScores.reduce((sum, cat) => sum + cat.value, 0) / 3;

    const drawSpeedometer = (value: number, size: number) => {
      const cx = size / 2;
      const cy = size / 2;
      const r = size * 0.35;
      const angle = -135 + (270 * value) / 100;
      const rad = (angle * Math.PI) / 180;
      const needleX = cx + r * 0.7 * Math.cos(rad);
      const needleY = cy + r * 0.7 * Math.sin(rad);
      const color = value >= 80 ? '#27ae60' : value >= 60 ? '#2ecc71' : value >= 40 ? '#f39c12' : '#e74c3c';

      return `
        <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
          <circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="#e0e0e0" stroke-width="8"/>
          <path d="M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}" stroke="#e74c3c" stroke-width="8" fill="none"/>
          <path d="M ${cx + r} ${cy} A ${r} ${r} 0 0 1 ${cx} ${cy - r}" stroke="#f39c12" stroke-width="8" fill="none"/>
          <path d="M ${cx} ${cy - r} A ${r} ${r} 0 0 1 ${cx - r} ${cy}" stroke="#27ae60" stroke-width="8" fill="none"/>
          <line x1="${cx}" y1="${cy}" x2="${needleX}" y2="${needleY}" stroke="${color}" stroke-width="3" stroke-linecap="round"/>
          <circle cx="${cx}" cy="${cy}" r="6" fill="${color}"/>
          <text x="${cx}" y="${cy + size * 0.25}" text-anchor="middle" font-size="18" font-weight="bold" fill="${color}">${value.toFixed(0)}%</text>
        </svg>
      `;
    };

    const renderGauge = (cat: any) => `
      <div style="text-align: center; padding: 10px; border: 1px solid #ddd; border-radius: 8px;">
        ${drawSpeedometer(cat.value, 140)}
        <div style="font-weight: 600; margin-top: 8px; font-size: 0.85rem;">${cat.name}</div>
      </div>
    `;

    return `
      <style>
        .speedometer-dashboard { width: 100%; padding: 20px; font-family: Arial, sans-serif; }
        .speed-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 15px; }
      </style>
      <div class="speedometer-dashboard" style="direction: ${language === 'he' ? 'rtl' : 'ltr'}">
        <h3 style="text-align: center;">🏎️ ${language === 'he' ? 'לוח מד מהירות - תצוגת טכומטר' : 'Speedometer Dashboard'}</h3>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px;">
          <div style="padding: 20px; background: linear-gradient(135deg, #2c3e50, #34495e); color: white; border-radius: 8px; text-align: center;">
            <h4>🏢 ${language === 'he' ? 'ביצועים עסקיים' : 'Business Performance'}</h4>
            ${drawSpeedometer(businessAverage, 180)}
          </div>
          <div style="padding: 20px; background: linear-gradient(135deg, #8e44ad, #9b59b6); color: white; border-radius: 8px; text-align: center;">
            <h4>👤 ${language === 'he' ? 'ביצועים אישיים' : 'Personal Performance'}</h4>
            ${drawSpeedometer(personalAverage, 180)}
          </div>
        </div>
        <div style="margin-bottom: 20px;">
          <h4>🏢 ${language === 'he' ? 'קטגוריות עסקיות' : 'Business Categories'}</h4>
          <div class="speed-grid">${businessScores.map(renderGauge).join('')}</div>
        </div>
        <div>
          <h4>👤 ${language === 'he' ? 'קטגוריות אישיות' : 'Personal Categories'}</h4>
          <div class="speed-grid">${personalScores.map(renderGauge).join('')}</div>
        </div>
      </div>
    `;
  };

  // Function to generate column chart dashboard HTML for printing
  const generateColumnChartDashboardHTML = (categoryScores: Record<string, number>, language: string) => {
    const categoryNames: Record<string, { hebrew: string; english: string }> = {
      'management': { hebrew: 'ניהול', english: 'Management' },
      'organization': { hebrew: 'ארגון', english: 'Organization' },
      'repeatSales': { hebrew: 'מכירות חוזרות', english: 'Repeat Sales' },
      'finance': { hebrew: 'כספים', english: 'Finance' },
      'operations': { hebrew: 'תפעול', english: 'Operations' },
      'qualityControl': { hebrew: 'בקרת איכות', english: 'Quality Control' },
      'newSales': { hebrew: 'מכירות חדשות', english: 'New Sales' },
      'stress': { hebrew: 'מתח', english: 'Stress' },
      'principles': { hebrew: 'עקרונות', english: 'Principles' },
      'helpAcceptance': { hebrew: 'קבלת עזרה', english: 'Help' },
    };

    const scores = Object.entries(categoryScores).map(([_, score], index) => {
      const categoryKeys = Object.keys(categoryNames);
      const categoryKey = categoryKeys[index] || 'management';
      return {
        name: language === 'he' ? categoryNames[categoryKey]?.hebrew : categoryNames[categoryKey]?.english,
        value: score as number
      };
    });

    const businessScores = scores.slice(0, 7);
    const personalScores = scores.slice(7, 10);
    const businessAverage = businessScores.reduce((sum, cat) => sum + cat.value, 0) / 7;
    const personalAverage = personalScores.reduce((sum, cat) => sum + cat.value, 0) / 3;

    const getColor = (score: number) => {
      const colors = score >= 80 ? { start: '#27ae60', end: '#2ecc71' } :
                     score >= 60 ? { start: '#2ecc71', end: '#52c77c' } :
                     score >= 40 ? { start: '#f39c12', end: '#f1c40f' } :
                     { start: '#e74c3c', end: '#e67e22' };
      return colors;
    };

    const renderColumn = (cat: any) => {
      const height = (cat.value / 100) * 200;
      const colors = getColor(cat.value);
      return `
        <div style="flex: 1; display: flex; flex-direction: column; align-items: center; min-width: 60px;">
          <div style="font-weight: bold; color: ${colors.start}; margin-bottom: 5px;">${cat.value.toFixed(0)}%</div>
          <div style="height: 200px; width: 100%; display: flex; flex-direction: column; justify-content: flex-end;">
            <div style="height: ${height}px; background: linear-gradient(180deg, ${colors.end}, ${colors.start}); border-radius: 8px 8px 0 0; box-shadow: 0 -2px 8px rgba(0,0,0,0.1);"></div>
          </div>
          <div style="font-weight: 600; margin-top: 8px; font-size: 0.75rem; text-align: center; max-width: 70px;">${cat.name}</div>
        </div>
      `;
    };

    return `
      <style>
        .column-dashboard { width: 100%; padding: 20px; font-family: Arial, sans-serif; }
      </style>
      <div class="column-dashboard" style="direction: ${language === 'he' ? 'rtl' : 'ltr'}">
        <h3 style="text-align: center;">📊 ${language === 'he' ? 'תרשים עמודות אנכי' : 'Vertical Column Chart'}</h3>
        <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-bottom: 30px;">
          <div style="padding: 20px; background: linear-gradient(135deg, #667eea, #764ba2); color: white; border-radius: 8px; text-align: center;">
            <h4>🏢 ${language === 'he' ? 'ממוצע עסקי' : 'Business Average'}</h4>
            <h2>${businessAverage.toFixed(1)}%</h2>
          </div>
          <div style="padding: 20px; background: linear-gradient(135deg, #f093fb, #f5576c); color: white; border-radius: 8px; text-align: center;">
            <h4>👤 ${language === 'he' ? 'ממוצע אישי' : 'Personal Average'}</h4>
            <h2>${personalAverage.toFixed(1)}%</h2>
          </div>
          <div style="padding: 20px; background: linear-gradient(135deg, #4facfe, #00f2fe); color: white; border-radius: 8px; text-align: center;">
            <h4>📊 ${language === 'he' ? 'ממוצע כללי' : 'Overall Average'}</h4>
            <h2>${((businessAverage * 7 + personalAverage * 3) / 10).toFixed(1)}%</h2>
          </div>
        </div>
        <div style="padding: 20px; border: 1px solid #ddd; border-radius: 8px; margin-bottom: 20px;">
          <h4>🏢 ${language === 'he' ? 'קטגוריות עסקיות' : 'Business Categories'}</h4>
          <div style="display: flex; gap: 10px; justify-content: space-around; margin-top: 20px;">${businessScores.map(renderColumn).join('')}</div>
        </div>
        <div style="padding: 20px; border: 1px solid #ddd; border-radius: 8px;">
          <h4>👤 ${language === 'he' ? 'קטגוריות אישיות' : 'Personal Categories'}</h4>
          <div style="display: flex; gap: 10px; justify-content: space-around; margin-top: 20px;">${personalScores.map(renderColumn).join('')}</div>
        </div>
      </div>
    `;
  };

  // Function to generate score card dashboard HTML for printing
  const generateScoreCardDashboardHTML = (categoryScores: Record<string, number>, language: string) => {
    const categoryNames: Record<string, { hebrew: string; english: string; icon: string }> = {
      'management': { hebrew: 'ניהול', english: 'Management', icon: '🎯' },
      'organization': { hebrew: 'ארגון', english: 'Organization', icon: '🏢' },
      'repeatSales': { hebrew: 'מכירות חוזרות', english: 'Repeat Sales', icon: '🔄' },
      'finance': { hebrew: 'כספים', english: 'Finance', icon: '💰' },
      'operations': { hebrew: 'תפעול', english: 'Operations', icon: '⚙️' },
      'qualityControl': { hebrew: 'בקרת איכות', english: 'Quality Control', icon: '✅' },
      'newSales': { hebrew: 'מכירות חדשות', english: 'New Sales', icon: '📈' },
      'stress': { hebrew: 'מתח', english: 'Stress', icon: '😰' },
      'principles': { hebrew: 'עקרונות', english: 'Principles', icon: '🧭' },
      'helpAcceptance': { hebrew: 'קבלת עזרה', english: 'Help', icon: '🤝' },
    };

    const scores = Object.entries(categoryScores).map(([_, score], index) => {
      const categoryKeys = Object.keys(categoryNames);
      const categoryKey = categoryKeys[index] || 'management';
      return {
        name: language === 'he' ? categoryNames[categoryKey]?.hebrew : categoryNames[categoryKey]?.english,
        icon: categoryNames[categoryKey]?.icon || '📊',
        value: score as number
      };
    });

    const businessScores = scores.slice(0, 7);
    const personalScores = scores.slice(7, 10);
    const businessAverage = businessScores.reduce((sum, cat) => sum + cat.value, 0) / 7;
    const personalAverage = personalScores.reduce((sum, cat) => sum + cat.value, 0) / 3;

    const getColor = (score: number) => score >= 80 ? '#27ae60' : score >= 60 ? '#2ecc71' : score >= 40 ? '#f39c12' : '#e74c3c';
    const getGrade = (score: number) => score >= 90 ? 'A+' : score >= 80 ? 'A' : score >= 70 ? 'B+' : score >= 60 ? 'B' : score >= 50 ? 'C+' : score >= 40 ? 'C' : 'D';
    const getStatus = (score: number) => {
      if (language === 'he') return score >= 80 ? 'ביצועים מצוינים' : score >= 60 ? 'ביצועים טובים' : score >= 40 ? 'ביצועים סבירים' : 'דורש התייחסות';
      return score >= 80 ? 'Excellent Performance' : score >= 60 ? 'Good Performance' : score >= 40 ? 'Fair Performance' : 'Needs Attention';
    };

    const renderCard = (cat: any) => `
      <div style="padding: 20px; border: 2px solid ${getColor(cat.value)}; background: linear-gradient(135deg, ${getColor(cat.value)}15, ${getColor(cat.value)}05); border-radius: 8px;">
        <div style="font-size: 36px; margin-bottom: 10px;">${cat.icon}</div>
        <h4 style="margin: 10px 0; min-height: 40px;">${cat.name}</h4>
        <div style="display: flex; align-items: baseline; gap: 10px; margin-bottom: 10px;">
          <span style="font-size: 48px; font-weight: bold; color: ${getColor(cat.value)};">${cat.value.toFixed(0)}</span>
          <span style="font-size: 20px; color: #666;">%</span>
          <span style="margin-left: auto; padding: 4px 12px; background: ${getColor(cat.value)}; color: white; border-radius: 12px; font-weight: bold;">${getGrade(cat.value)}</span>
        </div>
        <div style="height: 8px; background: #e0e0e0; border-radius: 4px; overflow: hidden; margin-bottom: 10px;">
          <div style="height: 100%; width: ${cat.value}%; background: ${getColor(cat.value)}; border-radius: 4px;"></div>
        </div>
        <div style="font-weight: 600; color: ${getColor(cat.value)}; font-size: 0.9rem;">${getStatus(cat.value)}</div>
      </div>
    `;

    return `
      <style>
        .scorecard-dashboard { width: 100%; padding: 20px; font-family: Arial, sans-serif; }
        .card-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 20px; }
      </style>
      <div class="scorecard-dashboard" style="direction: ${language === 'he' ? 'rtl' : 'ltr'}">
        <h3 style="text-align: center;">🏆 ${language === 'he' ? 'כרטיסי ציון מפורטים' : 'Detailed Score Cards'}</h3>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 30px;">
          <div style="padding: 30px; background: linear-gradient(135deg, #667eea, #764ba2); color: white; border-radius: 8px; text-align: center;">
            <h4>🏢 ${language === 'he' ? 'ביצועים עסקיים' : 'Business Performance'}</h4>
            <h1 style="margin: 10px 0;">${businessAverage.toFixed(1)}%</h1>
            <span style="padding: 6px 16px; background: rgba(255,255,255,0.3); border-radius: 12px; font-weight: bold;">${getGrade(businessAverage)}</span>
          </div>
          <div style="padding: 30px; background: linear-gradient(135deg, #f093fb, #f5576c); color: white; border-radius: 8px; text-align: center;">
            <h4>👤 ${language === 'he' ? 'ביצועים אישיים' : 'Personal Performance'}</h4>
            <h1 style="margin: 10px 0;">${personalAverage.toFixed(1)}%</h1>
            <span style="padding: 6px 16px; background: rgba(255,255,255,0.3); border-radius: 12px; font-weight: bold;">${getGrade(personalAverage)}</span>
          </div>
        </div>
        <div style="margin-bottom: 30px;">
          <h4>🏢 ${language === 'he' ? 'קטגוריות עסקיות' : 'Business Categories'}</h4>
          <div class="card-grid">${businessScores.map(renderCard).join('')}</div>
        </div>
        <div>
          <h4>👤 ${language === 'he' ? 'קטגוריות אישיות' : 'Personal Categories'}</h4>
          <div class="card-grid">${personalScores.map(renderCard).join('')}</div>
        </div>
      </div>
    `;
  };

  // Function to generate pilot-style gauges dashboard HTML for printing
  const generateGaugesDashboardHTML = (categoryScores: Record<string, number>, language: string) => {
    // Category name mappings with pilot-style descriptions
    const categoryNames: Record<string, { hebrew: string; english: string; hebrewDesc: string; englishDesc: string }> = {
      'management': {
        hebrew: 'ניהול',
        english: 'Executive',
        hebrewDesc: 'מערכת ניווט ראשית',
        englishDesc: 'Primary Navigation System'
      },
      'organization': {
        hebrew: 'ארגון',
        english: 'Organization',
        hebrewDesc: 'מערכת ייצוב טיסה',
        englishDesc: 'Flight Stabilization System'
      },
      'repeatSales': {
        hebrew: 'מכירות חוזרות',
        english: 'Repeat Sales',
        hebrewDesc: 'מד דלק',
        englishDesc: 'Fuel Gauge'
      },
      'finance': {
        hebrew: 'כספים',
        english: 'Finance',
        hebrewDesc: 'מד גובה כלכלי',
        englishDesc: 'Economic Altimeter'
      },
      'operations': {
        hebrew: 'תפעול',
        english: 'Operations',
        hebrewDesc: 'מד מהירות',
        englishDesc: 'Airspeed Indicator'
      },
      'qualityControl': {
        hebrew: 'בקרת איכות',
        english: 'Quality Control',
        hebrewDesc: 'מערכת אבחון',
        englishDesc: 'Diagnostic System'
      },
      'newSales': {
        hebrew: 'מכירות חדשות',
        english: 'New Sales',
        hebrewDesc: 'רדאר עסקי',
        englishDesc: 'Business Radar'
      },
      'stress': {
        hebrew: 'מתח',
        english: 'Stress',
        hebrewDesc: 'מד לחץ',
        englishDesc: 'Pressure Gauge'
      },
      'principles': {
        hebrew: 'עקרונות בסיסיים',
        english: 'Principles',
        hebrewDesc: 'מצפן מוסרי',
        englishDesc: 'Moral Compass'
      },
      'helpAcceptance': {
        hebrew: 'עזרה',
        english: 'Help',
        hebrewDesc: 'מערכת תקשורת',
        englishDesc: 'Communication System'
      },
    };

    // Process scores
    const scores = Object.entries(categoryScores).map(([_, score], index) => {
      const categoryKeys = Object.keys(categoryNames);
      const categoryKey = categoryKeys[index] || 'management';
      return {
        key: categoryKey,
        name: language === 'he' ? categoryNames[categoryKey]?.hebrew : categoryNames[categoryKey]?.english,
        description: language === 'he' ? categoryNames[categoryKey]?.hebrewDesc : categoryNames[categoryKey]?.englishDesc,
        value: score as number
      };
    });

    const businessScores = scores.slice(0, 7);
    const personalScores = scores.slice(7, 10);
    const businessAverage = businessScores.reduce((sum, cat) => sum + cat.value, 0) / 7;
    const personalAverage = personalScores.reduce((sum, cat) => sum + cat.value, 0) / 3;

    const getScoreColor = (score: number) => {
      if (score >= 80) return '#27ae60';
      if (score >= 60) return '#2ecc71';
      if (score >= 40) return '#f39c12';
      return '#e74c3c';
    };

    const getScoreLabel = (score: number) => {
      if (language === 'he') {
        if (score >= 80) return 'מעולה';
        if (score >= 60) return 'טוב';
        if (score >= 40) return 'בינוני';
        return 'דורש שיפור';
      } else {
        if (score >= 80) return 'Excellent';
        if (score >= 60) return 'Good';
        if (score >= 40) return 'Average';
        return 'Needs Improvement';
      }
    };

    const createAircraftGaugeHTML = (value: number, label: string, description: string, size: number = 100) => {
      const percentage = Math.min(100, Math.max(0, value));
      const isInControl = percentage >= 60;

      // Calculate angle for needle (270 degree sweep: -225 to 45)
      const startAngle = -225;
      const endAngle = 45;
      // Add 90 degree offset because needle is drawn pointing up
      const valueAngle = startAngle + (percentage / 100) * 270 + 90;

      const centerX = size / 2;
      const centerY = size / 2;
      const radius = size * 0.42;

      // Helper to create arc path
      const createArcPath = (startDeg: number, endDeg: number, r: number) => {
        const start = (startDeg * Math.PI) / 180;
        const end = (endDeg * Math.PI) / 180;
        const x1 = centerX + r * Math.cos(start);
        const y1 = centerY + r * Math.sin(start);
        const x2 = centerX + r * Math.cos(end);
        const y2 = centerY + r * Math.sin(end);
        const largeArc = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
        return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
      };

      const getZoneColor = (val: number) => {
        if (val >= 80) return '#22c55e';
        if (val >= 60) return '#84cc16';
        if (val >= 40) return '#fbbf24';
        if (val >= 20) return '#fb923c';
        return '#ef4444';
      };

      const needleColor = getZoneColor(percentage);

      return `
        <div style="display: inline-block; text-align: center; margin: 8px; vertical-align: top;">
          <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" style="overflow: visible;">
            <!-- Gradient definitions -->
            <defs>
              <radialGradient id="gauge-bg-${value}" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stop-color="#f8f9fa" />
                <stop offset="100%" stop-color="#e9ecef" />
              </radialGradient>
            </defs>

            <!-- Outer ring -->
            <circle cx="${centerX}" cy="${centerY}" r="${radius + size * 0.05}"
              fill="url(#gauge-bg-${value})" stroke="#dee2e6" stroke-width="1" />

            <!-- Inner background -->
            <circle cx="${centerX}" cy="${centerY}" r="${radius}"
              fill="#ffffff" stroke="#dee2e6" stroke-width="0.5" />

            <!-- Color zones -->
            <path d="${createArcPath(-225, -171, radius - size * 0.02)}"
              fill="none" stroke="#ef4444" stroke-width="${size * 0.03}" opacity="0.8" />
            <path d="${createArcPath(-171, -117, radius - size * 0.02)}"
              fill="none" stroke="#fb923c" stroke-width="${size * 0.03}" opacity="0.8" />
            <path d="${createArcPath(-117, -63, radius - size * 0.02)}"
              fill="none" stroke="#fbbf24" stroke-width="${size * 0.03}" opacity="0.8" />
            <path d="${createArcPath(-63, -9, radius - size * 0.02)}"
              fill="none" stroke="#84cc16" stroke-width="${size * 0.03}" opacity="0.8" />
            <path d="${createArcPath(-9, 45, radius - size * 0.02)}"
              fill="none" stroke="#22c55e" stroke-width="${size * 0.03}" opacity="0.8" />

            <!-- Scale marks and numbers -->
            ${[0, 20, 40, 60, 80, 100].map(num => {
              const angle = -225 + (num / 100) * 270;
              const radian = (angle * Math.PI) / 180;
              const innerR = radius - size * 0.08;
              const outerR = radius - size * 0.02;
              const labelR = radius - size * 0.18;
              const x1 = centerX + innerR * Math.cos(radian);
              const y1 = centerY + innerR * Math.sin(radian);
              const x2 = centerX + outerR * Math.cos(radian);
              const y2 = centerY + outerR * Math.sin(radian);
              const lx = centerX + labelR * Math.cos(radian);
              const ly = centerY + labelR * Math.sin(radian);

              return `
                <line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}"
                  stroke="${num === 60 ? '#fbbf24' : '#495057'}"
                  stroke-width="${num === 60 ? 2 : 1.5}" opacity="0.9" />
                <text x="${lx}" y="${ly}" fill="${num === 60 ? '#fbbf24' : '#212529'}"
                  font-size="${size * 0.09}" font-weight="${num === 60 ? 'bold' : 'normal'}"
                  text-anchor="middle" dominant-baseline="middle">${num}</text>
              `;
            }).join('')}

            <!-- 60% marker -->
            <polygon points="${centerX + (radius + size * 0.03) * Math.cos((-63 * Math.PI) / 180)},${centerY + (radius + size * 0.03) * Math.sin((-63 * Math.PI) / 180)} ${centerX + (radius + size * 0.08) * Math.cos((-63 * Math.PI) / 180) - 3},${centerY + (radius + size * 0.08) * Math.sin((-63 * Math.PI) / 180) + 3} ${centerX + (radius + size * 0.08) * Math.cos((-63 * Math.PI) / 180) + 3},${centerY + (radius + size * 0.08) * Math.sin((-63 * Math.PI) / 180) + 3}"
              fill="#fbbf24" />

            <!-- Gradient definitions for needle -->
            <defs>
              <linearGradient id="needleGrad-${value}" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stop-color="#ff6b6b" />
                <stop offset="30%" stop-color="#dc2626" />
                <stop offset="60%" stop-color="#b91c1c" />
                <stop offset="100%" stop-color="#7f1d1d" />
              </linearGradient>
              <radialGradient id="hubGrad-${value}" cx="50%" cy="30%" r="60%">
                <stop offset="0%" stop-color="#868e96" />
                <stop offset="100%" stop-color="#495057" />
              </radialGradient>
            </defs>

            <!-- Realistic Needle -->
            <g transform="translate(${centerX}, ${centerY})">
              <!-- Needle shadow for depth -->
              <g transform="rotate(${valueAngle + 1})">
                <polygon points="0,8 2,4 1,${-radius * 0.8} 0,${-radius * 0.82} -1,${-radius * 0.8} -2,4"
                  fill="rgba(0,0,0,0.4)" />
              </g>

              <!-- Main needle body -->
              <g transform="rotate(${valueAngle})">
                <!-- Needle base -->
                <ellipse cx="0" cy="0" rx="${size * 0.015}" ry="${size * 0.04}" fill="#991b1b" />
                <!-- Needle shaft with taper -->
                <polygon points="0,${size * 0.03} ${size * 0.008},0 ${size * 0.004},${-radius * 0.65} 0,${-radius * 0.82} -${size * 0.004},${-radius * 0.65} -${size * 0.008},0"
                  fill="url(#needleGrad-${value})" stroke="#7f1d1d" stroke-width="0.5" />
                <!-- Needle tip highlight -->
                <polygon points="0,${-radius * 0.82} ${size * 0.002},${-radius * 0.75} -${size * 0.002},${-radius * 0.75}"
                  fill="#fca5a5" opacity="0.9" />
              </g>

              <!-- Center hub with metallic effect -->
              <circle cx="0" cy="0" r="${size * 0.055}" fill="url(#hubGrad-${value})" stroke="#495057" stroke-width="0.5" />
              <circle cx="0" cy="0" r="${size * 0.045}" fill="#495057" stroke="#6c757d" stroke-width="0.3" />
              <circle cx="0" cy="0" r="${size * 0.035}" fill="#343a40" />
              <circle cx="0" cy="0" r="${size * 0.015}" fill="#495057" />
              <circle cx="0" cy="0" r="${size * 0.008}" fill="#6c757d" />
            </g>

            <!-- Digital display -->
            <rect x="${centerX - size * 0.18}" y="${centerY + size * 0.2}"
              width="${size * 0.36}" height="${size * 0.12}"
              fill="#f8f9fa" stroke="#adb5bd" stroke-width="1" rx="2" />
            <text x="${centerX}" y="${centerY + size * 0.26}"
              fill="#2e7d32" font-size="${size * 0.1}" font-weight="bold"
              text-anchor="middle" dominant-baseline="middle"
              style="font-family: monospace;">${percentage.toFixed(0)}</text>

            <!-- Status lights -->
            <circle cx="${centerX - size * 0.15}" cy="${centerY + size * 0.35}" r="${size * 0.02}"
              fill="${percentage < 60 ? '#ef4444' : '#adb5bd'}" stroke="#6c757d" stroke-width="0.5" />
            <circle cx="${centerX}" cy="${centerY + size * 0.35}" r="${size * 0.02}"
              fill="${percentage >= 40 && percentage < 80 ? '#fbbf24' : '#adb5bd'}" stroke="#6c757d" stroke-width="0.5" />
            <circle cx="${centerX + size * 0.15}" cy="${centerY + size * 0.35}" r="${size * 0.02}"
              fill="${percentage >= 60 ? '#22c55e' : '#adb5bd'}" stroke="#6c757d" stroke-width="0.5" />
          </svg>
          <div style="font-size: ${size * 0.32}px; font-weight: 600; margin-top: 5px; text-transform: uppercase;">
            ${label}
          </div>
          ${description ? `<div style="font-size: ${size * 0.24}px; font-style: italic; color: #666; margin-top: 3px;">
            ${description}
          </div>` : ''}
        </div>
      `;
    };

    return `
      <style>
        .gauges-container {
          width: 100%;
          padding: 5px;
        }
        .gauge-panel {
          margin-bottom: 15px;
          border: 1px solid #ddd;
          border-radius: 6px;
          padding: 8px;
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
        }
        .gauge-panel.personal {
          background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
        }
        .panel-header {
          text-align: center;
          padding: 4px;
          margin-bottom: 8px;
          border-radius: 4px;
          background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }
        .panel-header.personal {
          background: linear-gradient(135deg, #d35400 0%, #e67e22 100%);
        }
        .gauge-row {
          display: flex;
          align-items: flex-start;
          justify-content: space-around;
          gap: 10px;
        }
        .main-gauge-wrapper {
          flex: 0 0 auto;
        }
        .category-gauges {
          display: flex;
          flex: 1;
          justify-content: space-around;
          align-items: flex-start;
          gap: 5px;
        }
        .gauge-title {
          font-size: 12px;
          font-weight: bold;
        }
        .status-text {
          font-size: 9px;
          font-style: italic;
        }
      </style>
      <div class="gauges-container">
        <div class="gauge-panel">
          <div class="panel-header">
            <span class="gauge-title">✈️ ${language === 'he' ? 'פאנל ניווט עסקי' : 'Business Navigation Panel'}</span>
            <span class="status-text">(${language === 'he' ? 'מצב תפעולי' : 'Operational Status'})</span>
          </div>
          <div class="gauge-row">
            <div class="main-gauge-wrapper">
              ${createAircraftGaugeHTML(businessAverage, '', '', 80)}
            </div>
            <div class="category-gauges">
              ${businessScores.map(cat => createAircraftGaugeHTML(cat.value, cat.name || '', '', 50)).join('')}
            </div>
          </div>
        </div>
        <div class="gauge-panel personal">
          <div class="panel-header personal">
            <span class="gauge-title">👤 ${language === 'he' ? 'פאנל בקרה אישי' : 'Personal Control Panel'}</span>
            <span class="status-text">(${language === 'he' ? 'מצב אישי' : 'Personal Status'})</span>
          </div>
          <div class="gauge-row">
            <div class="main-gauge-wrapper">
              ${createAircraftGaugeHTML(personalAverage, '', '', 80)}
            </div>
            <div class="category-gauges">
              ${personalScores.map(cat => createAircraftGaugeHTML(cat.value, cat.name || '', '', 50)).join('')}
            </div>
          </div>
        </div>
      </div>
    `;
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow && selectedSession) {
      const participantName = selectedSession.participantName;
      const participantOrg = selectedSession.participantOrganization || '';
      const assessmentType = selectedSession.assessment?.type || 'Assessment';
      const completedDate = selectedSession.completedAt ?
        new Date(selectedSession.completedAt).toLocaleDateString() : 'N/A';

      // Determine which chart content to print based on user's current view
      let chartContent = '';

      if (chartViewType !== 'classic' && sessionDetails?.results?.categoryScores) {
        // Print experimental dashboards based on selected view type
        let dashboardHTML = '';
        switch (chartViewType) {
          case 'gauges':
            dashboardHTML = generateGaugesDashboardHTML(sessionDetails.results.categoryScores, language);
            break;
          case 'progress':
            dashboardHTML = generateProgressBarsDashboardHTML(sessionDetails.results.categoryScores, language);
            break;
          case 'circular':
            dashboardHTML = generateCircularDashboardHTML(sessionDetails.results.categoryScores, language);
            break;
          case 'radar':
            dashboardHTML = generateRadarChartDashboardHTML(sessionDetails.results.categoryScores, language);
            break;
          case 'horizontalBar':
            dashboardHTML = generateHorizontalBarDashboardHTML(sessionDetails.results.categoryScores, language);
            break;
          case 'heatMap':
            dashboardHTML = generateHeatMapDashboardHTML(sessionDetails.results.categoryScores, language);
            break;
          case 'speedometer':
            dashboardHTML = generateSpeedometerDashboardHTML(sessionDetails.results.categoryScores, language);
            break;
          case 'column':
            dashboardHTML = generateColumnChartDashboardHTML(sessionDetails.results.categoryScores, language);
            break;
          case 'scoreCard':
            dashboardHTML = generateScoreCardDashboardHTML(sessionDetails.results.categoryScores, language);
            break;
        }
        chartContent = `<div class="chart-container">${dashboardHTML}</div>`;
      } else {
        // Print classic chart image
        const sessionChartImage = chartImage || selectedSession.chartImage || '';
        chartContent = `
          <div class="chart-container">
            <img src="${sessionChartImage}" alt="Assessment Results Chart" />
          </div>
        `;
      }

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Assessment Results</title>
            <style>
              @page {
                margin: 0.5in;
                size: A4 landscape;
              }
              body {
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 0;
                direction: ${language === 'he' ? 'rtl' : 'ltr'};
              }
              .header {
                text-align: center;
                margin-bottom: 20px;
                padding-bottom: 10px;
                border-bottom: 2px solid #003399;
              }
              .header-text {
                font-size: 16px;
                color: #003399;
                font-weight: bold;
              }
              .chart-container {
                text-align: center;
              }
              .chart-container img {
                max-width: 100%;
                max-height: 5in;
                height: auto;
                width: auto;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <div class="header-text">
                TurnKeyOS.AI - ${assessmentType} Results - ${participantName}${participantOrg ? ` (${participantOrg})` : ''} - ${completedDate}
              </div>
            </div>
            ${chartContent}
          </body>
        </html>
      `);

      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    }
  };

  const handleFullAnalysisPrint = () => {
    if (!selectedSession || !sessionDetails) return;
    
    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    if (printWindow) {
      const sessionChartImage = chartImage || selectedSession.chartImage || '';
      const participantName = selectedSession.participantName;
      const participantOrg = selectedSession.participantOrganization || '';
      const participantEmail = selectedSession.participantEmail || '';
      const participantPhone = selectedSession.participantPhone || '';
      const participantPosition = selectedSession.participantPosition || '';
      const participantAddress = selectedSession.participantAddress || '';
      const participantEmployeeCount = selectedSession.participantEmployeeCount || '';
      const completedDate = selectedSession.completedAt ?
        new Date(selectedSession.completedAt).toLocaleDateString() : 'N/A';
      
      // Get the EpokAnalysisView content that's currently rendered in the DOM
      let analysisContent = '';
      const epokAnalysisElement = document.querySelector('[data-epok-analysis="true"]');
      
      if (epokAnalysisElement) {
        // Clone the element to manipulate without affecting the original
        const clonedElement = epokAnalysisElement.cloneNode(true) as HTMLElement;
        
        // Remove any interactive elements that shouldn't be printed
        const interactiveElements = clonedElement.querySelectorAll('button, .MuiButton-root, [role="button"]');
        interactiveElements.forEach(el => el.remove());
        
        // Get the inner HTML for printing
        analysisContent = clonedElement.innerHTML;
      } else {
        // Fallback content if we can't find the analysis
        analysisContent = `
          <div style="text-align: center; padding: 40px; color: #666;">
            <h3>${t.epokAnalysisUI?.detailedAnalysisTitle || 'Detailed Analysis'}</h3>
            <p>${t.epokAnalysisUI?.dataNotAvailable || 'Analysis data is being processed. Please ensure the detailed analysis is loaded before printing.'}</p>
          </div>
        `;
      }
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Full Assessment Analysis - ${participantName}</title>
            <meta charset="UTF-8">
            <style>
              @page { 
                margin: 1in 0.75in 1in 0.75in; 
                size: A4 portrait;
                @top-left {
                  content: "TurnKeyOS.AI";
                  font-size: 10pt;
                  color: #666;
                  font-family: Arial, sans-serif;
                }
                @top-center {
                  content: "${t.epokAnalysisUI?.title || 'EPOK Business Assessment Analysis'}";
                  font-size: 10pt;
                  color: #2c3e50;
                  font-weight: bold;
                  font-family: Arial, sans-serif;
                }
                @top-right {
                  content: "${participantName}";
                  font-size: 10pt;
                  color: #666;
                  font-family: Arial, sans-serif;
                }
                @bottom-left {
                  content: "${new Date().toLocaleDateString(language === 'he' ? 'he-IL' : 'en-US')}";
                  font-size: 9pt;
                  color: #999;
                  font-family: Arial, sans-serif;
                }
                @bottom-center {
                  content: "© TurnKeyOS.AI - Confidential";
                  font-size: 9pt;
                  color: #999;
                  font-family: Arial, sans-serif;
                }
                @bottom-right {
                  content: "Page " counter(page) " of " counter(pages);
                  font-size: 9pt;
                  color: #999;
                  font-family: Arial, sans-serif;
                }
              }
              body {
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 20px;
                direction: ${language === 'he' ? 'rtl' : 'ltr'};
                line-height: 1.6;
                color: #333;
                background: white;
              }
              .print-header {
                text-align: center;
                margin-bottom: 30px;
                padding-bottom: 20px;
                border-bottom: 4px solid #e74c3c;
                page-break-inside: avoid;
              }
              .print-header h1 {
                color: #2c3e50;
                font-weight: bold;
                margin: 0 0 10px 0;
                font-size: 1.8em;
              }
              .print-header p {
                color: #666;
                margin: 5px 0;
                font-size: 1.1em;
              }
              .participant-info {
                margin-bottom: 30px;
                padding: 20px;
                background-color: #f8f9fa;
                border-radius: 8px;
                page-break-inside: avoid;
              }
              .participant-info h3 {
                color: #34495e;
                margin: 0 0 15px 0;
                font-weight: bold;
              }
              .info-grid {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 15px;
              }
              .info-item {
                padding: 5px 0;
              }
              .info-item strong {
                color: #2c3e50;
              }
              .chart-section {
                text-align: center;
                margin-bottom: 30px;
                page-break-inside: avoid;
              }
              .chart-section h3 {
                color: #34495e;
                margin-bottom: 15px;
                font-weight: bold;
              }
              .chart-section img {
                max-width: 100%;
                height: auto;
                border-radius: 8px;
                border: 1px solid #ddd;
              }
              .analysis-content {
                margin-top: 20px;
              }
              
              /* Comprehensive styling to preserve EpokAnalysisView appearance */
              .analysis-content h1, .analysis-content h2, .analysis-content h3, .analysis-content h4, .analysis-content h5, .analysis-content h6 {
                color: #2c3e50 !important;
                font-weight: bold !important;
                margin: 20px 0 10px 0 !important;
              }
              
              /* Paper/Card components */
              .analysis-content .MuiPaper-root {
                background: #f8f9fa !important;
                border: 1px solid #ddd !important;
                border-radius: 8px !important;
                margin: 15px 0 !important;
                padding: 20px !important;
                page-break-inside: avoid !important;
                box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
              }
              
              .analysis-content .MuiCard-root {
                background: #fff !important;
                border: 1px solid #ddd !important;
                border-radius: 8px !important;
                margin: 10px 0 !important;
                padding: 15px !important;
                page-break-inside: avoid !important;
                box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
              }
              
              /* Typography variants */
              .analysis-content .MuiTypography-h3 {
                font-size: 1.5em !important;
                color: #2c3e50 !important;
                margin: 15px 0 10px 0 !important;
                font-weight: bold !important;
              }
              
              .analysis-content .MuiTypography-h4 {
                font-size: 1.3em !important;
                color: #34495e !important;
                margin: 10px 0 8px 0 !important;
                font-weight: bold !important;
              }
              
              .analysis-content .MuiTypography-h5 {
                font-size: 1.2em !important;
                color: #34495e !important;
                margin: 8px 0 6px 0 !important;
                font-weight: bold !important;
              }
              
              .analysis-content .MuiTypography-h6 {
                font-size: 1.1em !important;
                color: #495057 !important;
                margin: 6px 0 4px 0 !important;
                font-weight: bold !important;
              }
              
              .analysis-content .MuiTypography-body1 {
                font-size: 1em !important;
                line-height: 1.6 !important;
                margin: 8px 0 !important;
              }
              
              .analysis-content .MuiTypography-body2 {
                font-size: 0.9em !important;
                line-height: 1.5 !important;
                margin: 6px 0 !important;
              }
              
              .analysis-content .MuiTypography-subtitle1 {
                font-size: 1.1em !important;
                color: #666 !important;
                margin: 10px 0 !important;
              }
              
              .analysis-content .MuiTypography-subtitle2 {
                font-size: 1em !important;
                font-weight: bold !important;
                margin: 8px 0 5px 0 !important;
              }
              
              /* Chip styling */
              .analysis-content .MuiChip-root {
                display: inline-block !important;
                font-size: 0.8em !important;
                margin: 2px !important;
                padding: 4px 8px !important;
                border-radius: 16px !important;
                font-weight: 500 !important;
              }
              
              .analysis-content .MuiChip-colorSuccess {
                background-color: #d4edda !important;
                color: #155724 !important;
                border: 1px solid #c3e6cb !important;
              }
              
              .analysis-content .MuiChip-colorWarning {
                background-color: #fff3cd !important;
                color: #856404 !important;
                border: 1px solid #ffeaa7 !important;
              }
              
              .analysis-content .MuiChip-colorError {
                background-color: #f8d7da !important;
                color: #721c24 !important;
                border: 1px solid #f5c6cb !important;
              }
              
              /* Divider styling */
              .analysis-content .MuiDivider-root {
                border-color: #dee2e6 !important;
                margin: 15px 0 !important;
                opacity: 1 !important;
              }
              
              /* Specific color styling for different card types */
              .analysis-content .MuiCard-root[style*="backgroundColor: rgb(240, 249, 244)"],
              .analysis-content .MuiCard-root[style*="background-color: rgb(240, 249, 244)"] {
                background-color: #f0f9f4 !important;
                border-left: 4px solid #27ae60 !important;
              }
              
              .analysis-content .MuiCard-root[style*="backgroundColor: rgb(254, 249, 231)"],
              .analysis-content .MuiCard-root[style*="background-color: rgb(254, 249, 231)"] {
                background-color: #fef9e7 !important;
                border-left: 4px solid #f39c12 !important;
              }
              
              .analysis-content .MuiCard-root[style*="backgroundColor: rgb(253, 242, 242)"],
              .analysis-content .MuiCard-root[style*="background-color: rgb(253, 242, 242)"] {
                background-color: #fdf2f2 !important;
                border-left: 4px solid #e74c3c !important;
              }
              
              /* Growth potential section - green gradient */
              .analysis-content .MuiPaper-root[style*="linear-gradient(135deg, rgb(39, 174, 96), rgb(46, 204, 113))"],
              .analysis-content .MuiPaper-root[style*="linear-gradient(135deg, #27ae60, #2ecc71)"] {
                background: linear-gradient(135deg, #27ae60, #2ecc71) !important;
                color: white !important;
                padding: 25px !important;
                border-radius: 10px !important;
                text-align: center !important;
                margin: 20px 0 !important;
                page-break-inside: avoid !important;
              }
              
              .analysis-content .MuiPaper-root[style*="linear-gradient(135deg, rgb(39, 174, 96), rgb(46, 204, 113))"] .MuiTypography-root,
              .analysis-content .MuiPaper-root[style*="linear-gradient(135deg, #27ae60, #2ecc71)"] .MuiTypography-root {
                color: white !important;
              }
              
              /* Go back to the first approach but make it specific to avoid categories */
              .analysis-content .MuiGrid-container:first-of-type {
                display: flex !important;
                flex-direction: row !important;
                align-items: center !important;
                justify-content: center !important;
                flex-wrap: nowrap !important;
                width: 100% !important;
              }
              
              .analysis-content .MuiGrid-container:first-of-type .MuiGrid-item {
                flex: 1 !important;
                display: flex !important;
                flex-direction: column !important;
                align-items: center !important;
                justify-content: center !important;
                min-width: 0 !important;
                text-align: center !important;
              }
              
              /* Make sure only the FIRST grid container gets this treatment, not category grids */
              .analysis-content .MuiGrid-container:not(:first-of-type) {
                display: block !important;
                width: 100% !important;
              }
              
              .analysis-content .MuiGrid-container:not(:first-of-type) .MuiGrid-item {
                display: block !important;
                width: 100% !important;
                margin-bottom: 20px !important;
              }
              
              /* Blue gradient sections */
              .analysis-content .MuiPaper-root[style*="linear-gradient(135deg, rgb(52, 152, 219), rgb(41, 128, 185))"],
              .analysis-content .MuiPaper-root[style*="linear-gradient(135deg, #3498db, #2980b9)"] {
                background: linear-gradient(135deg, #3498db, #2980b9) !important;
                color: white !important;
                padding: 25px !important;
                border-radius: 10px !important;
                text-align: center !important;
                margin: 20px 0 !important;
                page-break-inside: avoid !important;
              }
              
              .analysis-content .MuiPaper-root[style*="linear-gradient(135deg, rgb(52, 152, 219), rgb(41, 128, 185))"] .MuiTypography-root,
              .analysis-content .MuiPaper-root[style*="linear-gradient(135deg, #3498db, #2980b9)"] .MuiTypography-root {
                color: white !important;
              }
              
              /* Summary section styling - light blue/green gradient */
              .analysis-content .MuiPaper-root[style*="linear-gradient(135deg, rgb(227, 242, 253)"],
              .analysis-content .MuiPaper-root[style*="linear-gradient(135deg, #e3f2fd"] {
                background: linear-gradient(135deg, #e3f2fd, #e8f5e8) !important;
                color: #2c3e50 !important;
                padding: 25px !important;
                border-radius: 10px !important;
                margin: 20px 0 !important;
                page-break-inside: avoid !important;
              }
              
              /* Red background sections (warnings/symptoms) */
              .analysis-content .MuiPaper-root[style*="rgba(231, 76, 60, 0.1)"] {
                background: rgba(231, 76, 60, 0.1) !important;
                border: 2px solid rgba(231, 76, 60, 0.3) !important;
                padding: 20px !important;
                border-radius: 8px !important;
                margin: 15px 0 !important;
              }
              
              /* Color-coded text */
              .analysis-content [style*="color: rgb(39, 174, 96)"],
              .analysis-content [style*="color: #27ae60"] {
                color: #27ae60 !important;
              }
              
              .analysis-content [style*="color: rgb(231, 76, 60)"],
              .analysis-content [style*="color: #e74c3c"] {
                color: #e74c3c !important;
              }
              
              .analysis-content [style*="color: rgb(243, 156, 18)"],
              .analysis-content [style*="color: #f39c12"] {
                color: #f39c12 !important;
              }
              
              .analysis-content [style*="color: rgb(52, 152, 219)"],
              .analysis-content [style*="color: #3498db"] {
                color: #3498db !important;
              }
              
              /* Emoji and icon preservation */
              .analysis-content [style*="font-size: 3em"],
              .analysis-content [style*="fontSize: 3em"] {
                font-size: 2em !important;
                margin-bottom: 10px !important;
              }
              
              .analysis-content [style*="font-size: 2.5em"],
              .analysis-content [style*="fontSize: 2.5em"] {
                font-size: 1.8em !important;
                margin-bottom: 8px !important;
              }
              .footer {
                margin-top: 40px;
                padding-top: 20px;
                border-top: 2px solid #ecf0f1;
                text-align: center;
                color: #7f8c8d;
                font-size: 0.9em;
                page-break-inside: avoid;
              }
              
              .page-break {
                page-break-before: always;
              }
              
              .page-break-avoid {
                page-break-inside: avoid;
              }
              
              @media print {
                .no-print { display: none !important; }
                
                body { 
                  -webkit-print-color-adjust: exact !important;
                  color-adjust: exact !important;
                  print-color-adjust: exact !important;
                  margin-top: 0.5in !important;
                  margin-bottom: 0.5in !important;
                }
                
                * {
                  -webkit-print-color-adjust: exact !important;
                  color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
                
                
                .analysis-content {
                  margin-top: 20px !important;
                  margin-bottom: 20px !important;
                }
                
                /* Better page break handling */
                h1, h2, h3, h4, h5, h6 {
                  page-break-after: avoid !important;
                }
                
                .MuiCard-root, .MuiPaper-root {
                  page-break-inside: avoid !important;
                  break-inside: avoid !important;
                }
                
                /* Force grid layouts to stay horizontal in print */
                .analysis-content * {
                  box-sizing: border-box !important;
                }
                
                /* Override any responsive breakpoints that might cause stacking - only for green gradient */
                @media print and (max-width: 1200px) {
                  .analysis-content .MuiPaper-root[style*="background: linear-gradient(135deg, rgb(39, 174, 96), rgb(46, 204, 113))"] .MuiGrid-container,
                  .analysis-content .MuiPaper-root[style*="linear-gradient(135deg, #27ae60, #2ecc71)"] .MuiGrid-container {
                    flex-direction: row !important;
                    flex-wrap: nowrap !important;
                  }
                  
                  .analysis-content .MuiPaper-root[style*="background: linear-gradient(135deg, rgb(39, 174, 96), rgb(46, 204, 113))"] .MuiGrid-item,
                  .analysis-content .MuiPaper-root[style*="linear-gradient(135deg, #27ae60, #2ecc71)"] .MuiGrid-item {
                    flex: 1 1 33.333% !important;
                    max-width: 33.333% !important;
                  }
                }
                
                /* Ensure category cards stay as individual blocks */
                .analysis-content .MuiCard-root {
                  width: 100% !important;
                  display: block !important;
                  margin-bottom: 20px !important;
                  page-break-inside: avoid !important;
                }
                
                /* Add page break before categories breakdown section */
                .analysis-content h2,
                .analysis-content .MuiTypography-h4 {
                  page-break-before: always !important;
                  margin-top: 0 !important;
                }
                
                /* More specific - target any h4 or h2 that comes after the summary sections */
                .analysis-content .MuiPaper-root ~ .MuiTypography-h4,
                .analysis-content .MuiPaper-root ~ h2,
                .analysis-content .MuiPaper-root ~ h4 {
                  page-break-before: always !important;
                  margin-top: 0 !important;
                }
              }
            </style>
          </head>
          <body>
            <div class="print-header">
              <h1>${t.epokAnalysisUI?.title || 'EPOK Business Assessment Analysis'} - ${participantName}</h1>
              <p>${t.epokAnalysisUI?.subtitle || 'Comprehensive Business Potential Analysis'}</p>
            </div>
            
            <div class="participant-info">
              <h3>${t['admin.participant.info'] || 'Participant Information'}</h3>
              <div class="info-grid">
                <div class="info-item"><strong>${t['admin.name'] || 'Name'}:</strong> ${participantName}</div>
                <div class="info-item"><strong>${t['admin.email'] || 'Email'}:</strong> ${participantEmail}</div>
                <div class="info-item"><strong>${t['admin.phone'] || 'Phone'}:</strong> ${participantPhone || 'N/A'}</div>
                <div class="info-item"><strong>${t['admin.organization'] || 'Organization'}:</strong> ${participantOrg || 'N/A'}</div>
                <div class="info-item"><strong>${t['admin.position'] || 'Position'}:</strong> ${participantPosition || 'N/A'}</div>
                <div class="info-item"><strong>${t['admin.address'] || 'Address'}:</strong> ${participantAddress || 'N/A'}</div>
                <div class="info-item"><strong>${t['admin.employeeCount'] || 'Employee Count'}:</strong> ${participantEmployeeCount || 'N/A'}</div>
                <div class="info-item"><strong>${t['admin.completed'] || 'Completed'}:</strong> ${completedDate}</div>
              </div>
            </div>
            
            ${(() => {
              // Respect user's current view choice
              if (chartViewType !== 'classic' && sessionDetails?.results?.categoryScores) {
                let dashboardHTML = '';
                switch (chartViewType) {
                  case 'gauges':
                    dashboardHTML = generateGaugesDashboardHTML(sessionDetails.results.categoryScores, language);
                    break;
                  case 'progress':
                    dashboardHTML = generateProgressBarsDashboardHTML(sessionDetails.results.categoryScores, language);
                    break;
                  case 'circular':
                    dashboardHTML = generateCircularDashboardHTML(sessionDetails.results.categoryScores, language);
                    break;
                  case 'radar':
                    dashboardHTML = generateRadarChartDashboardHTML(sessionDetails.results.categoryScores, language);
                    break;
                  case 'horizontalBar':
                    dashboardHTML = generateHorizontalBarDashboardHTML(sessionDetails.results.categoryScores, language);
                    break;
                  case 'heatMap':
                    dashboardHTML = generateHeatMapDashboardHTML(sessionDetails.results.categoryScores, language);
                    break;
                  case 'speedometer':
                    dashboardHTML = generateSpeedometerDashboardHTML(sessionDetails.results.categoryScores, language);
                    break;
                  case 'column':
                    dashboardHTML = generateColumnChartDashboardHTML(sessionDetails.results.categoryScores, language);
                    break;
                  case 'scoreCard':
                    dashboardHTML = generateScoreCardDashboardHTML(sessionDetails.results.categoryScores, language);
                    break;
                }
                return `<div class="chart-section">${dashboardHTML}</div>`;
              } else if (sessionChartImage) {
                return `<div class="chart-section"><img src="${sessionChartImage}" alt="Assessment Results Chart" /></div>`;
              }
              return '';
            })()}
            
            <div class="analysis-content">
              ${analysisContent}
            </div>
          </body>
        </html>
      `);
      
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 1000);
    }
  };

  const handleViewDetails = async (session: Session) => {
    setSelectedSession(session);
    setDetailsOpen(true);
    setSessionDetails(null);
    setChartImage(null);
    setChartLoading(false);

    // Fetch complete session details with responses
    try {
      const detailsResponse = await wrappedAdminSessionAPI.getSessionDetails(session.id);
      setSessionDetails(detailsResponse.data);
      console.log('Session details fetched:', detailsResponse.data);
    } catch (error) {
      console.error('Failed to fetch session details:', error);
    }

    // Generate chart for display and printing
    if (session.status === 'completed') {
      setChartLoading(true);
      try {
        const response = await wrappedAdminSessionAPI.generateChart(session.id, language);
        const data = response.data as { chartImage: string };
        setChartImage(data.chartImage);

        const updatedSession = { ...session, chartImage: data.chartImage };
        setSelectedSession(updatedSession);
      } catch (error) {
        console.error('Failed to generate chart:', error);
      } finally {
        setChartLoading(false);
      }
    }
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedSession(null);
    setSessionDetails(null);
    setChartImage(null);
    setChartViewType('classic'); // Reset to default view
  };

  const handleParticipantSelect = (sessionId: string, checked: boolean) => {
    const newSelectedParticipants = new Set(selectedParticipants);
    if (checked) {
      newSelectedParticipants.add(sessionId);
    } else {
      newSelectedParticipants.delete(sessionId);
    }
    setSelectedParticipants(newSelectedParticipants);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const visibleSessionIds = sessions.map((session: Session) => session.id);
      setSelectedParticipants(new Set(visibleSessionIds));
    } else {
      setSelectedParticipants(new Set());
    }
  };

  const handleOrganizationChange = (organization: string) => {
    setSelectedOrganization(organization);
    setSelectedParticipants(new Set());
  };

  const handleGenerateComparison = async () => {
    if (selectedParticipants.size < 2) return;
    
    setComparisonLoading(true);
    setComparisonDialogOpen(true);
    
    try {
      const sessionIds = Array.from(selectedParticipants);
      const response = await wrappedAdminSessionAPI.generateComparisonChart(sessionIds, language);
      const data = response.data as { chartImage: string };
      setComparisonChartImage(data.chartImage);
    } catch (error) {
      console.error('Failed to generate comparison chart:', error);
      setComparisonChartImage(null);
    } finally {
      setComparisonLoading(false);
    }
  };

  const handleCloseComparison = () => {
    setComparisonDialogOpen(false);
    setComparisonChartImage(null);
    setComparisonLoading(false);
  };

  // Edit participant handlers
  const handleEditParticipant = (session: Session) => {
    setSessionToEdit(session);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSessionToEdit(null);
  };

  const handleEditSuccess = (updatedSession: any) => {
    // Update the session in the local state, preserving existing fields
    setSessions(prev => prev.map(s => s.id === updatedSession.id ? { ...s, ...updatedSession } : s));
  };

  // Delete session handlers
  const handleDeleteSession = (session: Session) => {
    setSessionToDelete(session);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSessionToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!sessionToDelete) return;
    
    setDeletingLoading(true);
    try {
      const deleteSessions = httpsCallable(functions, 'deleteSessions');
      const result = await deleteSessions({ sessionIds: [sessionToDelete.id] });
      
      const response = result.data as any;
      if (response.success) {
        toast.success(`${t['delete.session.success']} ${sessionToDelete.participantName}`);
        // Remove from local state
        setSessions(prev => prev.filter(s => s.id !== sessionToDelete.id));
        handleCloseDeleteDialog();
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error(t['delete.session.error']);
    } finally {
      setDeletingLoading(false);
    }
  };

  // Bulk delete handlers
  const handleBulkDelete = () => {
    if (selectedParticipants.size === 0) return;
    setBulkDeleteDialogOpen(true);
  };

  const handleCloseBulkDeleteDialog = () => {
    setBulkDeleteDialogOpen(false);
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedParticipants.size === 0) return;
    
    setDeletingLoading(true);
    try {
      const deleteSessions = httpsCallable(functions, 'deleteSessions');
      const sessionIds = Array.from(selectedParticipants);
      const result = await deleteSessions({ sessionIds });
      
      const response = result.data as any;
      if (response.success) {
        toast.success(`${t['bulk.delete.success']} ${response.deletedCount} ${t['admin.completed.sessions'].toLowerCase()}`);
        // Remove from local state
        setSessions(prev => prev.filter(s => !selectedParticipants.has(s.id)));
        setSelectedParticipants(new Set());
        handleCloseBulkDeleteDialog();
      }
    } catch (error) {
      console.error('Error bulk deleting sessions:', error);
      toast.error(t['bulk.delete.error']);
    } finally {
      setDeletingLoading(false);
    }
  };

  const handlePrintComparison = () => {
    if (!comparisonChartImage) return;
    
    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Participant Comparison Results</title>
            <style>
              @page { 
                margin: 0.5in; 
                size: A4 landscape;
              }
              body {
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 0;
                direction: ${language === 'he' ? 'rtl' : 'ltr'};
              }
              .header {
                text-align: center;
                margin-bottom: 20px;
                padding-bottom: 10px;
                border-bottom: 2px solid #003399;
              }
              .header-text {
                font-size: 16px;
                color: #003399;
                font-weight: bold;
              }
              .chart-container {
                text-align: center;
              }
              .chart-container img {
                max-width: 100%;
                max-height: 6in;
                height: auto;
                width: auto;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <div class="header-text">
                TurnKeyOS.AI - Participant Comparison - ${selectedParticipants.size} Participants - ${new Date().toLocaleDateString()}
              </div>
            </div>
            <div class="chart-container">
              <img src="${comparisonChartImage}" alt="Participant Comparison Chart" />
            </div>
          </body>
        </html>
      `);
      
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    }
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>{t['admin.loading']}</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" component="h1">
            <Assessment sx={{ mr: 2, verticalAlign: 'middle' }} />
            {t['admin.dashboard']}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {t['admin.subtitle']}
          </Typography>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <ToggleButtonGroup
                  value={sessionFilter}
                  exclusive
                  onChange={(_, value) => value && setSessionFilter(value)}
                  size="small"
                  fullWidth
                >
                  <ToggleButton value="completed" sx={{ gap: 1 }}>
                    <CheckCircle fontSize="small" />
                    {t['admin.filter.completed'] || 'Completed'}
                  </ToggleButton>
                  <ToggleButton value="unfinished" sx={{ gap: 1 }}>
                    <HourglassEmpty fontSize="small" />
                    {t['admin.filter.unfinished'] || 'Unfinished'}
                  </ToggleButton>
                  <ToggleButton value="leads" sx={{ gap: 1 }}>
                    <ContactPhone fontSize="small" />
                    {t['admin.filter.leads'] || 'Leads'}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t['admin.filter.organization']}</InputLabel>
                  <Select
                    value={selectedOrganization}
                    onChange={(e) => handleOrganizationChange(e.target.value)}
                    label={t['admin.filter.organization']}
                  >
                    <MenuItem value="">{t['admin.filter.all.organizations']}</MenuItem>
                    {organizations.map((org: string) => (
                      <MenuItem key={org} value={org}>
                        {org}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  {t['admin.filter.showing']} {sessions.length} {t['admin.filter.sessions']}
                  {selectedOrganization && ` - ${selectedOrganization}`}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t['admin.total.sessions']}
                </Typography>
                <Typography variant="h3" color="primary">
                  {sessions.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t['admin.epoc.assessments']}
                </Typography>
                <Typography variant="h3" color="primary">
                  {sessions.filter((s: Session) => s.assessment?.type === '360').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Participant Comparison Section */}
        <Accordion sx={{ mb: 4 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Compare sx={{ mr: 2 }} />
              <Typography variant="h5">
                {t['admin.comparison.title']}
                {selectedParticipants.size > 0 && (
                  <Chip 
                    label={`${selectedParticipants.size} selected`}
                    size="small" 
                    sx={{ ml: 2 }} 
                    color={selectedParticipants.size >= 2 ? 'primary' : 'default'}
                  />
                )}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t['admin.comparison.subtitle']}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedParticipants.size === sessions.length && sessions.length > 0}
                      indeterminate={selectedParticipants.size > 0 && selectedParticipants.size < sessions.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  }
                  label={t['admin.comparison.select.all']}
                />
                <Button
                  variant="contained"
                  startIcon={<TrendingUp />}
                  onClick={handleGenerateComparison}
                  disabled={selectedParticipants.size < 2}
                  size="small"
                >
                  {t['admin.comparison.generate']} ({selectedParticipants.size})
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={deletingLoading ? <CircularProgress size={16} /> : <DeleteOutline />}
                  onClick={handleBulkDelete}
                  disabled={selectedParticipants.size === 0 || deletingLoading}
                  size="small"
                >
                  {deletingLoading ? t['bulk.delete.deleting'] : `${t['bulk.delete.button']} (${selectedParticipants.size})`}
                </Button>
                {selectedParticipants.size > 0 && (
                  <Button
                    variant="outlined"
                    onClick={() => setSelectedParticipants(new Set())}
                    size="small"
                  >
                    {t['admin.comparison.clear']}
                  </Button>
                )}
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Quiz Leads Table */}
        {sessionFilter === 'leads' && (
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                {t['admin.quiz.leads'] || 'Quiz Leads'}
              </Typography>
              {leadsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>{t['admin.name'] || 'Name'}</strong></TableCell>
                        <TableCell><strong>{t['admin.email'] || 'Email'}</strong></TableCell>
                        <TableCell><strong>{t['admin.phone'] || 'Phone'}</strong></TableCell>
                        <TableCell><strong>{t['admin.lead.challenge'] || 'Challenge'}</strong></TableCell>
                        <TableCell><strong>{t['admin.lead.urgency'] || 'Urgency'}</strong></TableCell>
                        <TableCell><strong>{t['admin.lead.status'] || 'Status'}</strong></TableCell>
                        <TableCell><strong>{t['admin.lead.wants360'] || '360?'}</strong></TableCell>
                        <TableCell><strong>{t['admin.lead.date'] || 'Date'}</strong></TableCell>
                        <TableCell><strong>{t['admin.actions'] || 'Actions'}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {quizLeads.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                            <Typography color="text.secondary">
                              {t['admin.no.leads'] || 'No leads yet'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        quizLeads.map((lead: QuizLead) => (
                          <TableRow key={lead.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {lead.firstName} {lead.lastName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{lead.email}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{lead.phone}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {lead.challenges || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={lead.urgency || '-'}
                                size="small"
                                color={lead.urgency?.includes('אתמול') || lead.urgency?.includes('Yesterday') ? 'error' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={lead.status}
                                size="small"
                                color={
                                  lead.status === 'new' ? 'info' :
                                  lead.status === 'contacted' ? 'warning' :
                                  lead.status === 'qualified' ? 'success' :
                                  lead.status === 'converted' ? 'success' :
                                  'default'
                                }
                              />
                            </TableCell>
                            <TableCell>
                              {lead.wantsDeepAnalysis ? (
                                <Chip label="✓" color="success" size="small" />
                              ) : (
                                <Chip label="✗" color="default" size="small" />
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {lead.createdAt ? new Date(lead.createdAt).toLocaleDateString() : '-'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {lead.createdAt ? new Date(lead.createdAt).toLocaleTimeString() : ''}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Tooltip title={t['admin.view.details'] || 'View Details'}>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedLead(lead);
                                    setLeadDetailsOpen(true);
                                  }}
                                >
                                  <Visibility />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        )}

        {/* Sessions Table */}
        {sessionFilter !== 'leads' && (
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              {sessionFilter === 'completed'
                ? t['admin.completed.sessions']
                : (t['admin.unfinished.sessions'] || 'Unfinished Sessions')}
            </Typography>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedParticipants.size === sessions.length && sessions.length > 0}
                        indeterminate={selectedParticipants.size > 0 && selectedParticipants.size < sessions.length}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </TableCell>
                    <TableCell><strong>{t['admin.participant']}</strong></TableCell>
                    <TableCell><strong>{t['admin.assessment']}</strong></TableCell>
                    <TableCell><strong>{t['admin.organization']}</strong></TableCell>
                    <TableCell>
                      <strong>
                        {sessionFilter === 'completed'
                          ? t['admin.completed']
                          : (t['admin.progress'] || 'Progress')}
                      </strong>
                    </TableCell>
                    <TableCell><strong>{t['admin.actions']}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                          {t['admin.no.sessions']}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sessions.map((session: Session) => (
                      <TableRow key={session.id} hover selected={selectedParticipants.has(session.id)}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedParticipants.has(session.id)}
                            onChange={(e) => handleParticipantSelect(session.id, e.target.checked)}
                          />
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {session.participantName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {session.participantEmail}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={session.assessment?.type || 'N/A'} 
                            color={session.assessment?.type === '360' ? 'primary' : 'secondary'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {session.participantOrganization || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {sessionFilter === 'completed' ? (
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {session.completedAt ?
                                  new Date(session.completedAt).toLocaleDateString() :
                                  'N/A'
                                }
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {session.completedAt ?
                                  new Date(session.completedAt).toLocaleTimeString() :
                                  ''
                                }
                              </Typography>
                            </Box>
                          ) : (
                            <Box>
                              <Chip
                                label={session.status === 'started'
                                  ? (t['admin.status.started'] || 'Started')
                                  : (t['admin.status.in_progress'] || 'In Progress')}
                                size="small"
                                color={session.status === 'started' ? 'default' : 'warning'}
                                sx={{ mb: 0.5 }}
                              />
                              <Typography variant="caption" display="block" color="text.secondary">
                                {session.createdAt || session.startedAt ?
                                  new Date(session.createdAt || session.startedAt).toLocaleDateString() + ' ' +
                                  new Date(session.createdAt || session.startedAt).toLocaleTimeString() :
                                  ''
                                }
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {sessionFilter === 'completed' && (
                              <Tooltip title={t['tooltip.view.details']}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewDetails(session)}
                                  color="primary"
                                >
                                  <Visibility />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title={t['tooltip.edit.participant']}>
                              <IconButton
                                size="small"
                                onClick={() => handleEditParticipant(session)}
                                color="secondary"
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t['tooltip.delete.session']}>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteSession(session)}
                                color="error"
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
        )}

        {/* Session Details Dialog */}
        <Dialog
          open={detailsOpen}
          onClose={handleCloseDetails}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Person sx={{ mr: 1 }} />
              {t['admin.session.details']} - {selectedSession?.participantName}
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedSession && (
              <Box sx={{ py: 2 }}>
                <Typography variant="h6" gutterBottom>
                  <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {t['admin.participant.info']}
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">{t['admin.name']}</Typography>
                    <Typography variant="body1">{selectedSession.participantName}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">{t['admin.email']}</Typography>
                    <Typography variant="body1">{selectedSession.participantEmail}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">{t['admin.phone']}</Typography>
                    <Typography variant="body1">{selectedSession.participantPhone || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">{t['admin.organization']}</Typography>
                    <Typography variant="body1">{selectedSession.participantOrganization || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">{t['admin.position']}</Typography>
                    <Typography variant="body1">{selectedSession.participantPosition || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">{t['admin.address']}</Typography>
                    <Typography variant="body1">{selectedSession.participantAddress || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">{t['admin.employeeCount']}</Typography>
                    <Typography variant="body1">{selectedSession.participantEmployeeCount || 'N/A'}</Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
                    {t['admin.assessment.results']}
                  </Typography>

                  {/* View selector dropdown */}
                  {selectedSession.status === 'completed' && sessionDetails?.results?.categoryScores && (
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel>{language === 'he' ? 'סוג תצוגה' : 'View Type'}</InputLabel>
                      <Select
                        value={chartViewType}
                        label={language === 'he' ? 'סוג תצוגה' : 'View Type'}
                        onChange={(e) => setChartViewType(e.target.value as any)}
                      >
                        <MenuItem value="classic">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BarChart fontSize="small" />
                            {language === 'he' ? 'תרשים קלאסי' : 'Classic Chart'}
                          </Box>
                        </MenuItem>
                        <MenuItem value="gauges">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Science fontSize="small" />
                            {language === 'he' ? 'מדדי טיסה (ניסיוני)' : 'Flight Gauges (Experimental)'}
                          </Box>
                        </MenuItem>
                        <MenuItem value="progress">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrendingUp fontSize="small" />
                            {language === 'he' ? 'פסי התקדמות (ניסיוני)' : 'Progress Bars (Experimental)'}
                          </Box>
                        </MenuItem>
                        <MenuItem value="circular">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Assessment fontSize="small" />
                            {language === 'he' ? 'מעגלי התקדמות (ניסיוני)' : 'Circular Progress (Experimental)'}
                          </Box>
                        </MenuItem>
                        <MenuItem value="radar">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BarChart fontSize="small" />
                            {language === 'he' ? 'תרשים רדאר (ניסיוני)' : 'Radar Chart (Experimental)'}
                          </Box>
                        </MenuItem>
                        <MenuItem value="horizontalBar">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrendingUp fontSize="small" />
                            {language === 'he' ? 'עמודות אופקיות (ניסיוני)' : 'Horizontal Bars (Experimental)'}
                          </Box>
                        </MenuItem>
                        <MenuItem value="heatMap">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Assessment fontSize="small" />
                            {language === 'he' ? 'מפת חום (ניסיוני)' : 'Heat Map (Experimental)'}
                          </Box>
                        </MenuItem>
                        <MenuItem value="speedometer">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Science fontSize="small" />
                            {language === 'he' ? 'מדי מהירות (ניסיוני)' : 'Speedometer (Experimental)'}
                          </Box>
                        </MenuItem>
                        <MenuItem value="column">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BarChart fontSize="small" />
                            {language === 'he' ? 'עמודות אנכיות (ניסיוני)' : 'Column Chart (Experimental)'}
                          </Box>
                        </MenuItem>
                        <MenuItem value="scoreCard">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Assessment fontSize="small" />
                            {language === 'he' ? 'כרטיסי ציון (ניסיוני)' : 'Score Cards (Experimental)'}
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Box>

                {selectedSession.status === 'completed' && (
                  <>
                    {/* Classic Chart View */}
                    {chartViewType === 'classic' && (
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        p: 2,
                        backgroundColor: 'background.default',
                        borderRadius: 2,
                        minHeight: '400px',
                        alignItems: 'center'
                      }}>
                        {chartLoading ? (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CircularProgress size={24} sx={{ mr: 2 }} />
                            <Typography>{t['admin.generating.chart'] || 'Generating chart...'}</Typography>
                          </Box>
                        ) : chartImage ? (
                          <img
                            src={chartImage}
                            alt="Assessment Results Chart"
                            style={{
                              maxWidth: '100%',
                              height: 'auto',
                              borderRadius: '8px'
                            }}
                          />
                        ) : (
                          <Typography color="text.secondary">
                            {t['admin.chart.unavailable'] || 'Chart unavailable'}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Experimental view: Flight Gauges Dashboard */}
                    {chartViewType === 'gauges' && sessionDetails?.results?.categoryScores && (
                      <GaugesDashboard
                        categoryScores={sessionDetails.results.categoryScores}
                        language={language}
                      />
                    )}

                    {/* Experimental view: Progress Bars Dashboard */}
                    {chartViewType === 'progress' && sessionDetails?.results?.categoryScores && (
                      <ProgressBarsDashboard
                        categoryScores={sessionDetails.results.categoryScores}
                        language={language}
                      />
                    )}

                    {/* Experimental view: Circular Progress Dashboard */}
                    {chartViewType === 'circular' && sessionDetails?.results?.categoryScores && (
                      <CircularDashboard
                        categoryScores={sessionDetails.results.categoryScores}
                        language={language}
                      />
                    )}

                    {/* Experimental view: Radar Chart Dashboard */}
                    {chartViewType === 'radar' && sessionDetails?.results?.categoryScores && (
                      <RadarChartDashboard
                        categoryScores={sessionDetails.results.categoryScores}
                        language={language}
                      />
                    )}

                    {/* Experimental view: Horizontal Bar Dashboard */}
                    {chartViewType === 'horizontalBar' && sessionDetails?.results?.categoryScores && (
                      <HorizontalBarDashboard
                        categoryScores={sessionDetails.results.categoryScores}
                        language={language}
                      />
                    )}

                    {/* Experimental view: Heat Map Dashboard */}
                    {chartViewType === 'heatMap' && sessionDetails?.results?.categoryScores && (
                      <HeatMapDashboard
                        categoryScores={sessionDetails.results.categoryScores}
                        language={language}
                      />
                    )}

                    {/* Experimental view: Speedometer Dashboard */}
                    {chartViewType === 'speedometer' && sessionDetails?.results?.categoryScores && (
                      <SpeedometerDashboard
                        categoryScores={sessionDetails.results.categoryScores}
                        language={language}
                      />
                    )}

                    {/* Experimental view: Column Chart Dashboard */}
                    {chartViewType === 'column' && sessionDetails?.results?.categoryScores && (
                      <ColumnChartDashboard
                        categoryScores={sessionDetails.results.categoryScores}
                        language={language}
                      />
                    )}

                    {/* Experimental view: Score Card Dashboard */}
                    {chartViewType === 'scoreCard' && sessionDetails?.results?.categoryScores && (
                      <ScoreCardDashboard
                        categoryScores={sessionDetails.results.categoryScores}
                        language={language}
                      />
                    )}
                  </>
                )}

                {/* EPOK Analysis Section */}
                {sessionDetails && selectedSession?.assessment?.type === '360' && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" gutterBottom>
                      <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
                      {t.epokAnalysisUI.detailedAnalysisTitle}
                    </Typography>
                    <EpokAnalysisView 
                      session={sessionDetails} 
                      language={language}
                    />
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handlePrint}
              startIcon={<Print />}
              variant="outlined"
              sx={{ 
                mx: 1                
              }}
            >
              <span style={{ marginLeft: '10px', marginRight: '10px' }}>{t['admin.print']}</span>
            </Button>
            <Button 
              onClick={handleFullAnalysisPrint}
              startIcon={<PrintOutlined />}
              variant="contained"
              disabled={!sessionDetails || selectedSession?.assessment?.type !== '360'}
              sx={{ 
                mx: 1,                
              }}
            >
              <span style={{ marginLeft: '10px', marginRight: '10px' }}>{t['admin.printfull']}</span>              
            </Button>
            <Button onClick={handleCloseDetails}>{t['admin.close']}</Button>
          </DialogActions>
        </Dialog>

        {/* Lead Details Dialog */}
        <Dialog
          open={leadDetailsOpen}
          onClose={() => setLeadDetailsOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Person sx={{ mr: 1 }} />
              {t['admin.lead.details'] || 'Lead Details'} - {selectedLead?.firstName} {selectedLead?.lastName}
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedLead && (
              <Box sx={{ py: 2 }}>
                {/* Contact Information */}
                <Typography variant="h6" gutterBottom>
                  <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {t['admin.participant.info'] || 'Contact Information'}
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">{t['admin.name'] || 'Name'}</Typography>
                    <Typography variant="body1">{selectedLead.firstName} {selectedLead.lastName}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">{t['admin.email'] || 'Email'}</Typography>
                    <Typography variant="body1">{selectedLead.email}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">{t['admin.phone'] || 'Phone'}</Typography>
                    <Typography variant="body1">{selectedLead.phone}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">{t['admin.lead.date'] || 'Date'}</Typography>
                    <Typography variant="body1">
                      {selectedLead.createdAt ? new Date(selectedLead.createdAt).toLocaleString() : '-'}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Quiz Answers */}
                <Typography variant="h6" gutterBottom>
                  <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {t['admin.lead.answers'] || 'Quiz Answers'}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Q1: Challenges */}
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      1. {language === 'he' ? 'מה האתגר הכי גדול שאתה מתמודד איתו כרגע בעסק?' : 'What is the biggest challenge you are facing?'}
                    </Typography>
                    <Typography variant="body2">
                      {Array.isArray(selectedLead.challenges)
                        ? selectedLead.challenges.join(', ')
                        : selectedLead.challenges || selectedLead.answers?.q1_challenges?.join?.(', ') || selectedLead.answers?.q1_challenges || '-'}
                    </Typography>
                  </Paper>

                  {/* Q2: Duration */}
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      2. {language === 'he' ? 'כמה זמן אתה כבר מנסה לפתור את זה?' : 'How long have you been trying to solve this?'}
                    </Typography>
                    <Typography variant="body2">
                      {selectedLead.duration || selectedLead.answers?.q2_duration || '-'}
                    </Typography>
                  </Paper>

                  {/* Q3: What tried */}
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      3. {language === 'he' ? 'מה ניסית עד עכשיו כדי לפתור את זה?' : 'What have you tried so far?'}
                    </Typography>
                    <Typography variant="body2">
                      {Array.isArray(selectedLead.tried)
                        ? selectedLead.tried.join(', ')
                        : selectedLead.tried || selectedLead.answers?.q3_tried?.join?.(', ') || selectedLead.answers?.q3_tried || '-'}
                    </Typography>
                  </Paper>

                  {/* Q4: Why not worked (conditional) */}
                  {(selectedLead.whyNotWorked || selectedLead.answers?.q4_why_not_worked) && (
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="subtitle2" color="primary" gutterBottom>
                        4. {language === 'he' ? 'מה לדעתך הסיבה שזה לא הסתדר עד היום?' : 'Why hasn\'t it worked out until today?'}
                      </Typography>
                      <Typography variant="body2">
                        {selectedLead.whyNotWorked || selectedLead.answers?.q4_why_not_worked}
                      </Typography>
                    </Paper>
                  )}

                  {/* Q5: Why now (conditional) */}
                  {(selectedLead.whyNow || selectedLead.answers?.q5_why_now) && (
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="subtitle2" color="primary" gutterBottom>
                        5. {language === 'he' ? 'מה גורם לך לפעול דווקא עכשיו?' : 'What\'s causing you to act now?'}
                      </Typography>
                      <Typography variant="body2">
                        {selectedLead.whyNow || selectedLead.answers?.q5_why_now}
                      </Typography>
                    </Paper>
                  )}

                  {/* Q6: Urgency */}
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      6. {language === 'he' ? 'עד כמה דחוף לך לטפל בזה?' : 'How urgent is it for you to handle this?'}
                    </Typography>
                    <Typography variant="body2">
                      {selectedLead.urgency || selectedLead.answers?.q6_urgency || '-'}
                    </Typography>
                  </Paper>

                  {/* Q7a: Business field */}
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      7א. {language === 'he' ? 'באיזה תחום העסק שלך?' : 'What field is your business in?'}
                    </Typography>
                    <Typography variant="body2">
                      {selectedLead.businessField || selectedLead.answers?.q7a_business_field || '-'}
                    </Typography>
                  </Paper>

                  {/* Q7b: Team size */}
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      7ב. {language === 'he' ? 'יש לך עובדים/צוות?' : 'Do you have employees/team?'}
                    </Typography>
                    <Typography variant="body2">
                      {selectedLead.teamSize || selectedLead.answers?.q7b_team_size || '-'}
                    </Typography>
                  </Paper>

                  {/* Q8: Most important */}
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      8. {language === 'he' ? 'כשאתה חושב על שיפור העסק, מה הכי חשוב לך?' : 'When thinking about improvement, what\'s most important?'}
                    </Typography>
                    <Typography variant="body2">
                      {selectedLead.important || selectedLead.answers?.q8_important || '-'}
                    </Typography>
                  </Paper>

                  {/* Q8a: Potential revenue (conditional) */}
                  {(selectedLead.potentialRevenue || selectedLead.answers?.q8a_potential_revenue) && (
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="subtitle2" color="primary" gutterBottom>
                        8א. {language === 'he' ? 'מה פוטנציאל ההכנסה החודשי שאתה צופה?' : 'What monthly income do you expect?'}
                      </Typography>
                      <Typography variant="body2">
                        {selectedLead.potentialRevenue || selectedLead.answers?.q8a_potential_revenue}
                      </Typography>
                    </Paper>
                  )}

                  {/* Q8b: Current revenue */}
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      8ב. {language === 'he' ? 'באיזה סדר גודל ההכנסות של העסק כיום?' : 'Current business income size?'}
                    </Typography>
                    <Typography variant="body2">
                      {selectedLead.currentRevenue || selectedLead.answers?.q8b_current_revenue || '-'}
                    </Typography>
                  </Paper>

                  {/* Q9: Consulting */}
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      9. {language === 'he' ? 'האם אי פעם ניסית ייעוץ עסקי?' : 'Have you ever tried business consulting?'}
                    </Typography>
                    <Typography variant="body2">
                      {selectedLead.consulting || selectedLead.answers?.q9_consulting || '-'}
                    </Typography>
                  </Paper>

                  {/* Q10: Expectations */}
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      10. {language === 'he' ? 'מה היית מצפה לקבל מיועץ עסקי?' : 'What would you expect from a consultant?'}
                    </Typography>
                    <Typography variant="body2">
                      {Array.isArray(selectedLead.expectations)
                        ? selectedLead.expectations.join(', ')
                        : selectedLead.expectations || selectedLead.answers?.q10_expectations?.join?.(', ') || selectedLead.answers?.q10_expectations || '-'}
                    </Typography>
                  </Paper>

                  {/* Q11: Deep analysis */}
                  <Paper sx={{ p: 2, bgcolor: selectedLead.wantsDeepAnalysis ? 'success.50' : 'grey.50' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      11. {language === 'he' ? 'תרצה למלא שאלון ניתוח עסקי של 100 שאלות?' : 'Would you like to fill out a 100-question business analysis?'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">
                        {selectedLead.deepAnalysis || selectedLead.answers?.q11_deep_analysis || '-'}
                      </Typography>
                      {selectedLead.wantsDeepAnalysis && (
                        <Chip label={language === 'he' ? 'רוצה 360!' : 'Wants 360!'} color="success" size="small" />
                      )}
                    </Box>
                  </Paper>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLeadDetailsOpen(false)}>{t['admin.close'] || 'Close'}</Button>
          </DialogActions>
        </Dialog>

        {/* Comparison Chart Dialog */}
        <Dialog
          open={comparisonDialogOpen}
          onClose={handleCloseComparison}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Compare sx={{ mr: 1 }} />
              Participant Comparison
              {selectedParticipants.size > 0 && (
                <Chip 
                  label={`${selectedParticipants.size} ${t['admin.comparison.participants']}`}
                  size="small" 
                  sx={{ ml: 2 }} 
                  color="primary"
                />
              )}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              p: 2,
              backgroundColor: 'background.default',
              borderRadius: 2,
              minHeight: '400px',
              alignItems: 'center'
            }}>
              {comparisonLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={24} sx={{ mr: 2 }} />
                  <Typography>{t['admin.comparison.generating']}</Typography>
                </Box>
              ) : comparisonChartImage ? (
                <img
                  src={comparisonChartImage}
                  alt="Participant Comparison Chart"
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: '8px'
                  }}
                />
              ) : (
                <Typography color="text.secondary">
                  {t['admin.comparison.unavailable']}
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handlePrintComparison}
              startIcon={<Print />}
              variant="outlined"
              disabled={!comparisonChartImage}
            >
              {t['admin.print']}
            </Button>
            <Button onClick={handleCloseComparison}>{t['admin.close']}</Button>
          </DialogActions>
        </Dialog>
        
        {/* Edit Participant Dialog */}
        <EditParticipantDialog
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
          session={sessionToEdit}
          onSuccess={handleEditSuccess}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
          <DialogTitle>{t['delete.session.title']}</DialogTitle>
          <DialogContent>
            <Typography>
              {t['delete.session.message']}{' '}
              <strong>{sessionToDelete?.participantName}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t['delete.session.warning']}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog} disabled={deletingLoading}>
              {t['delete.session.cancel']}
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
              disabled={deletingLoading}
              startIcon={deletingLoading ? <CircularProgress size={16} /> : <Delete />}
            >
              {deletingLoading ? t['delete.session.deleting'] : t['delete.session.delete']}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Delete Confirmation Dialog */}
        <Dialog open={bulkDeleteDialogOpen} onClose={handleCloseBulkDeleteDialog}>
          <DialogTitle>{t['bulk.delete.title']}</DialogTitle>
          <DialogContent>
            <Typography>
              {t['bulk.delete.message']} <strong>{selectedParticipants.size}</strong> {t['admin.completed.sessions'].toLowerCase()}?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t['bulk.delete.warning']}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseBulkDeleteDialog} disabled={deletingLoading}>
              {t['bulk.delete.cancel']}
            </Button>
            <Button
              onClick={handleConfirmBulkDelete}
              color="error"
              variant="contained"
              disabled={deletingLoading}
              startIcon={deletingLoading ? <CircularProgress size={16} /> : <DeleteOutline />}
            >
              {deletingLoading ? t['bulk.delete.deleting'] : `${t['bulk.delete.delete']} ${selectedParticipants.size} ${t['admin.completed.sessions']}`}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default AdminPage;